// src/App.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import ProductList from "./components/ProductList";
import Checkout from "./components/Checkout";
import Signup from "./components/Signup";
import Login from "./components/Login";
import AdminDashboard from "./components/AdminDashboard";
import SellerDashboard from "./components/SellerDashboard";
import CartPage from "./components/CartPage";
import ProductDetail from "./components/ProductDetail";
import WishlistPage from "./components/WishlistPage";
import OrdersPage from "./components/OrdersPage";
import BookLibrary from "./components/BookLibrary";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import ShippingPolicy from "./components/ShippingPolicy";
import ReturnPolicy from "./components/ReturnPolicy";
import ContactUs from "./components/ContactUs";
import AboutUs from "./components/AboutUs";
import { saveAuth, loadAuth, clearAuth, getCurrentUser, isAdmin } from "./auth";
import logo from './assets/logo.jpeg';

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const CartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.4 5.2 16.4H17M17 13V16.4M9 19C9 19.5523 8.55228 20 8 20C7.44772 20 7 19.5523 7 19C7 18.4477 7.44772 18 8 18C8.55228 18 9 18.4477 9 19ZM17 19C17 19.5523 16.5523 20 16 20C15.4477 20 15 19.5523 15 19C15 18.4477 15.4477 18 16 18C16.5523 18 17 18.4477 17 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const OrdersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 12H15M12 16H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BookIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HomeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RocketIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 15L9 12L12 16L16 12L12 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M19 9L20 5H4L5 9L8 12L12 8L16 12L19 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 13L4 19H20L19 13L16 12L12 16L8 12L5 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 16V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CrownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 19L5 9L9.5 13.5L12 6L14.5 13.5L19 9L22 19H2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HeartIcon = ({ filled = false }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);

const TwitterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
);

const LinkedInIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
);

const LibraryIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SellerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11M5 9H19V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PolicyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ShippingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 3H1V16H16V3ZM16 8H20L23 11V16H16V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.5 21C6.88071 21 8 19.8807 8 18.5C8 17.1193 6.88071 16 5.5 16C4.11929 16 3 17.1193 3 18.5C3 19.8807 4.11929 21 5.5 21ZM18.5 21C19.8807 21 21 19.8807 21 18.5C21 17.1193 19.8807 16 18.5 16C17.1193 16 16 17.1193 16 18.5C16 19.8807 17.1193 21 18.5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ContactIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DonationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Data ─────────────────────────────────────────────────────────────────────
const countryCurrencyMap = {
  "IN":"INR","US":"USD","GB":"GBP","EU":"EUR","CA":"CAD","AU":"AUD",
  "JP":"JPY","CN":"CNY","AE":"AED","SA":"SAR","DE":"EUR","FR":"EUR",
  "IT":"EUR","ES":"EUR","BR":"BRL","MX":"MXN","RU":"RUB","KR":"KRW",
  "SG":"SGD","MY":"MYR","TH":"THB"
};

const currencyData = {
  "INR":{ symbol:"₹", name:"Indian Rupee", country:"India", flag:"🇮🇳" },
  "USD":{ symbol:"$", name:"US Dollar", country:"United States", flag:"🇺🇸" },
  "EUR":{ symbol:"€", name:"Euro", country:"European Union", flag:"🇪🇺" },
  "GBP":{ symbol:"£", name:"British Pound", country:"United Kingdom", flag:"🇬🇧" },
  "CAD":{ symbol:"C$", name:"Canadian Dollar", country:"Canada", flag:"🇨🇦" },
  "AUD":{ symbol:"A$", name:"Australian Dollar", country:"Australia", flag:"🇦🇺" },
  "JPY":{ symbol:"¥", name:"Japanese Yen", country:"Japan", flag:"🇯🇵" },
  "CNY":{ symbol:"¥", name:"Chinese Yuan", country:"China", flag:"🇨🇳" },
  "AED":{ symbol:"د.إ", name:"UAE Dirham", country:"UAE", flag:"🇦🇪" },
  "SAR":{ symbol:"﷼", name:"Saudi Riyal", country:"Saudi Arabia", flag:"🇸🇦" }
};

// ─── API ───────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://shop.neoteeforge.com/neotee-backend';
const API_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

const getImageUrl = (p) => {
  if (!p) return 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=600&fit=crop';
  if (p.startsWith('http')) return p;
  return `${API_BASE_URL}${p.startsWith('/') ? '' : '/'}${p}`;
};

const getThumbnailUrl = (n) => {
  if (!n) return 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=200&h=300&fit=crop';
  if (n.startsWith('http')) return n;
  if (n.startsWith('/uploads/')) return `${API_BASE_URL}${n}`;
  return `${API_BASE_URL}/uploads/images/${n}`;
};

const getPrimaryImage = (product) => {
  if (!product) return null;
  if (product.images && Array.isArray(product.images)) {
    const pri = product.images.find(img => img.is_primary === 1) || product.images[0];
    if (pri && pri.image_name) return getThumbnailUrl(pri.thumbnail_name || `thumb_${pri.image_name}`);
  }
  if (product.thumbnail_url) return getImageUrl(product.thumbnail_url);
  if (product.image) return getImageUrl(product.image);
  return 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=600&fit=crop';
};

const apiService = {
  _get: async (url, token) => {
    const h = token ? { ...API_HEADERS, Authorization: `Bearer ${token}` } : API_HEADERS;
    const r = await fetch(url, { headers: h });
    return r.json();
  },
  _post: async (url, body, token) => {
    const h = token ? { ...API_HEADERS, Authorization: `Bearer ${token}` } : API_HEADERS;
    const r = await fetch(url, { method:'POST', headers: h, body: JSON.stringify(body) });
    return r.json();
  },
  _put: async (url, body, token) => {
    const h = { ...API_HEADERS, Authorization: `Bearer ${token}` };
    const r = await fetch(url, { method:'PUT', headers: h, body: JSON.stringify(body) });
    return r.json();
  },
  _del: async (url, token) => {
    const r = await fetch(url, { method:'DELETE', headers: { ...API_HEADERS, Authorization: `Bearer ${token}` } });
    return r.json();
  },
  tok: () => loadAuth()?.token,
  login: (e, p) => apiService._post(`${API_BASE_URL}/api/login`, {email:e, password:p}).catch(() => ({success:false,error:'Network error'})),
  signup: (d) => apiService._post(`${API_BASE_URL}/api/signup`, d).catch(() => ({success:false,error:'Network error'})),
  getAllProducts: () => apiService._get(`${API_BASE_URL}/api/products`).catch(() => ({success:false,products:[]})),
  getProduct: (id) => apiService._get(`${API_BASE_URL}/api/products/${id}`).catch(() => ({success:false,product:null})),
  searchProducts: (q) => apiService._get(`${API_BASE_URL}/api/products/search?q=${encodeURIComponent(q)}`).catch(() => ({success:false,products:[]})),
  getAllCategories: () => apiService._get(`${API_BASE_URL}/api/categories`).catch(() => ({success:false,categories:[]})),
  getUserLibrary: () => apiService._get(`${API_BASE_URL}/api/books/library`, apiService.tok()).catch(() => ({success:false,books:[]})),
  getBook: (id) => apiService._get(`${API_BASE_URL}/api/books/${id}`).catch(() => ({success:false,book:null})),
  downloadBook: (id) => apiService._get(`${API_BASE_URL}/api/books/${id}/download`, apiService.tok()).catch(() => ({success:false})),
  readOnline: (id) => apiService._get(`${API_BASE_URL}/api/books/${id}/read`, apiService.tok()).catch(() => ({success:false})),
  createOrder: (d) => apiService._post(`${API_BASE_URL}/api/orders`, d, apiService.tok()).catch(() => ({success:false})),
  getUserOrders: () => apiService._get(`${API_BASE_URL}/api/orders`, apiService.tok()).catch(() => ({success:false,orders:[]})),
  getOrderById: (id) => apiService._get(`${API_BASE_URL}/api/orders/${id}`, apiService.tok()).catch(() => ({success:false})),
  verifyPayment: (d) => apiService._post(`${API_BASE_URL}/api/orders/verify-payment`, d, apiService.tok()).catch(() => ({success:false})),
  getUserProfile: () => apiService._get(`${API_BASE_URL}/api/user/profile`, apiService.tok()).catch(() => ({success:false})),
  getAllProductsAdmin: () => apiService._get(`${API_BASE_URL}/api/admin/products`, apiService.tok()).catch(() => ({success:false,products:[]})),
  createProduct: (d) => apiService._post(`${API_BASE_URL}/api/admin/products`, d, apiService.tok()).catch(() => ({success:false})),
  updateProduct: (id,d) => apiService._put(`${API_BASE_URL}/api/admin/products/${id}`, d, apiService.tok()).catch(() => ({success:false})),
  deleteProduct: (id) => apiService._del(`${API_BASE_URL}/api/admin/products/${id}`, apiService.tok()).catch(() => ({success:false})),
  getDashboardStats: () => apiService._get(`${API_BASE_URL}/api/admin/dashboard`, apiService.tok()).catch(() => ({success:false})),
  getAllOrdersAdmin: () => apiService._get(`${API_BASE_URL}/api/admin/orders`, apiService.tok()).catch(() => ({success:false,orders:[]})),
  getSellerProducts: () => apiService._get(`${API_BASE_URL}/api/seller/products`, apiService.tok()).catch(() => ({success:false,products:[]})),
  getSellerStats: () => apiService._get(`${API_BASE_URL}/api/seller/dashboard`, apiService.tok()).catch(() => ({success:false})),
  sendContactMessage: (d) => apiService._post(`${API_BASE_URL}/api/contact`, d).catch(() => ({success:false})),
  subscribeNewsletter: (email) => apiService._post(`${API_BASE_URL}/api/newsletter/subscribe`, {email}).catch(() => ({success:false})),
  getExchangeRates: async () => {
    try {
      const d = await apiService._get(`${API_BASE_URL}/api/exchange-rates`);
      return d.success && d.rates ? d.rates : null;
    } catch { return null; }
  },
};

// ─── CurrencySelector ─────────────────────────────────────────────────────────
function CurrencySelector({ currency, exchangeRates, onCurrencyChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top:0, left:0, width:0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const calcPos = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 5, left: r.left, width: Math.max(r.width, 252) });
    }
  };

  const toggle = () => { if (!isOpen) calcPos(); setIsOpen(v => !v); };
  const select = (code) => { onCurrencyChange(code); setIsOpen(false); };

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e) => {
      if ((!btnRef.current || !btnRef.current.contains(e.target)) &&
          (!menuRef.current || !menuRef.current.contains(e.target)))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const update = () => calcPos();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [isOpen]);

  const info = currencyData[currency] || currencyData['INR'];

  return (
    <>
      <button ref={btnRef} onClick={toggle} aria-haspopup="listbox" aria-expanded={isOpen}
        style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.4rem 0.7rem',
          borderRadius:'8px', border:`1.5px solid ${isOpen ? '#e63946' : '#e2e8f0'}`,
          background:'white', color:'#334155', fontFamily:'inherit', fontWeight:600,
          fontSize:'0.8rem', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0, minWidth:'74px',
          boxShadow: isOpen ? '0 0 0 3px rgba(230,57,70,0.12)' : '0 2px 6px rgba(0,0,0,0.06)',
          transition:'all 0.2s ease' }}>
        <span style={{fontSize:'1rem',lineHeight:1}}>{info.flag}</span>
        <span style={{fontWeight:700}}>{currency}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
          style={{transition:'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', marginLeft:'2px'}}>
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && (
        <div ref={menuRef} role="listbox"
          style={{ position:'fixed', top:pos.top, left:pos.left, minWidth:pos.width,
            zIndex:2147483647, background:'#fff', borderRadius:'14px',
            boxShadow:'0 20px 60px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.1)',
            border:'1px solid #e8ecf0', overflowY:'auto', maxHeight:'320px',
            animation:'currencyDropIn 0.18s ease-out both' }}>
          {Object.entries(currencyData).map(([code, cInfo]) => {
            const active = currency === code;
            return (
              <div key={code} role="option" aria-selected={active} onClick={() => select(code)}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'0.6rem 0.9rem', cursor:'pointer', borderBottom:'1px solid #f5f7fa',
                  gap:'0.75rem', background: active ? 'rgba(230,57,70,0.06)' : 'transparent',
                  borderLeft: active ? '3px solid #e63946' : '3px solid transparent',
                  transition:'background 0.12s' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background='#fff5f5'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background='transparent'; }}>
                <div style={{display:'flex',alignItems:'center',gap:'0.6rem',flex:1}}>
                  <span style={{fontSize:'1.2rem',lineHeight:1}}>{cInfo.flag}</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:'0.83rem',color:'#1e293b'}}>{code}</div>
                    <div style={{fontSize:'0.68rem',color:'#64748b',marginTop:'1px'}}>{cInfo.name}</div>
                  </div>
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div style={{fontWeight:700,fontSize:'0.92rem',color:'#e63946'}}>{cInfo.symbol}</div>
                  <div style={{fontSize:'0.63rem',color:'#059669',marginTop:'2px'}}>
                    {exchangeRates[code] != null ? `1 INR = ${exchangeRates[code].toFixed(4)}` : '...'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes currencyDropIn{from{opacity:0;transform:translateY(-8px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </>
  );
}

// ─── AppContent ───────────────────────────────────────────────────────────────
function AppContent() {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currency, setCurrency] = useState("INR");
  const [auth, setAuth] = useState(loadAuth());
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [typingText, setTypingText] = useState("");
  const [typingIndex, setTypingIndex] = useState(0);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [activeCategory, setActiveCategory] = useState("all");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [animatedStats, setAnimatedStats] = useState({ readers:0, books:0, authors:0, customers:0 });
  const [statsAnimated, setStatsAnimated] = useState(false);
  const [userLocation, setUserLocation] = useState({ country:"India", countryCode:"IN", city:"", region:"", timezone:"Asia/Kolkata", currency:"INR" });
  const [exchangeRates, setExchangeRates] = useState({ INR:1, USD:0.012, EUR:0.011, GBP:0.0095, CAD:0.016, AUD:0.018, JPY:1.78, CNY:0.087, AED:0.044, SAR:0.045 });
  const [geolocationError, setGeolocationError] = useState(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [totalBooksCount, setTotalBooksCount] = useState(0);
  const [donationAmount, setDonationAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationMessage, setDonationMessage] = useState("");
  const [donationName, setDonationName] = useState("");
  const [donationEmail, setDonationEmail] = useState("");
  const [isProcessingDonation, setIsProcessingDonation] = useState(false);

  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Updated typing phrases for printable activity books
  const typingPhrases = [
    "Printable Activity Books for Smart Kids",
    "Download • Print • Learn at Home",
    "Instant PDF Download • No Shipping",
    "Make Learning Fun and Effective"
  ];

  // ── helpers ────────────────────────────────────────────────────────────────
  const convertPrice = useCallback((priceInINR) => {
    if (priceInINR == null || priceInINR === 0) return `${currencyData[currency]?.symbol || '₹'}0`;
    const rate = exchangeRates[currency];
    if (!rate) return `${currencyData[currency]?.symbol || '₹'}${priceInINR}`;
    const converted = priceInINR * rate;
    const info = currencyData[currency];
    if (!info) return `₹${Math.round(converted)}`;
    switch (currency) {
      case 'INR': return `₹${Math.round(converted)}`;
      case 'JPY': case 'CNY': return `${info.symbol}${Math.round(converted)}`;
      case 'AED': case 'SAR': return `${info.symbol} ${converted.toFixed(2)}`;
      default: return `${info.symbol}${converted.toFixed(2)}`;
    }
  }, [currency, exchangeRates]);

  const getPriceBreakdown = (priceInINR, productData = null) => {
    if (!priceInINR && priceInINR !== 0) return { mrp:0, offerPrice:0, discountPercentage:0, gstAmount:0, totalPrice:0, gstPercentage:18 };
    if (productData?.pricing) {
      const bp = productData.pricing;
      return { mrp: bp.mrp||bp.original_price||priceInINR, offerPrice: bp.discounted_price||bp.base_price||priceInINR, discountPercentage: bp.effective_discount||bp.savings_percentage||0, gstAmount: bp.gst_amount||0, totalPrice: bp.final_price||priceInINR, gstPercentage: bp.gst_percentage||18, ...bp };
    }
    const mrp = Math.round(priceInINR * 1.3);
    const discount = 10 + (priceInINR % 11);
    const offerPrice = Math.round(mrp * (1 - discount / 100));
    const gstAmount = Math.round(offerPrice * 0.18);
    return { mrp, offerPrice, discountPercentage: discount, gstAmount, totalPrice: offerPrice + gstAmount, gstPercentage: 18 };
  };

  const getCategoryColor = (id) => ({ 1:"#059669",2:"#1d4ed8",3:"#7c3aed",4:"#dc2626",5:"#0891b2",6:"#d97706",7:"#db2777",8:"#4f46e5",9:"#0d9488",10:"#7c2d12" }[id] || "#e63946");
  const getCategoryName = (id) => { if (!id) return "Unknown"; const c = categories.find(c => c.id === id); return c ? c.name : `Category ${id}`; };
  const getTotalCartItems = () => (Array.isArray(cartItems) ? cartItems : []).reduce((t,i) => t+(i.quantity||0), 0);
  const getOrdersCount = () => (Array.isArray(userOrders) ? userOrders.length : 0);
  const getUserDisplayName = () => auth?.user?.name || auth?.user?.email?.split('@')[0] || 'User';
  const userIsAdmin = isAdmin();
  const userIsSeller = auth?.user?.role === 'seller' || auth?.user?.role === 'admin';

  const getCurrentView = () => {
    const p = location.pathname;
    if (p === '/') return 'home'; if (p.startsWith('/product/')) return 'product-detail';
    if (p === '/signup') return 'signup'; if (p === '/login') return 'login';
    if (p === '/admin') return 'admin'; if (p === '/seller/dashboard') return 'seller-dashboard';
    if (p === '/cart') return 'cart'; if (p === '/checkout') return 'checkout';
    if (p === '/wishlist') return 'wishlist'; if (p === '/orders') return 'orders';
    if (p === '/library') return 'library'; if (p === '/privacy-policy') return 'privacy-policy';
    if (p === '/terms-of-service') return 'terms-of-service'; if (p === '/shipping-policy') return 'shipping-policy';
    if (p === '/return-policy') return 'return-policy'; if (p === '/contact-us') return 'contact-us';
    if (p === '/about-us') return 'about-us'; return 'home';
  };
  const currentView = getCurrentView();

  // ── data loaders ───────────────────────────────────────────────────────────
  const loadUserOrders = async () => {
    if (!auth?.token) { setUserOrders([]); return; }
    const d = await apiService.getUserOrders();
    setUserOrders(d.success && Array.isArray(d.orders) ? d.orders : []);
  };

  const loadCategoriesFromBackend = async () => {
    const d = await apiService.getAllCategories();
    if (d.success && Array.isArray(d.categories)) {
      setCategories([{ id:"all", name:"All Books", description:"Browse all books", color:"#e63946" }, ...d.categories.map(c => ({ ...c, color: getCategoryColor(c.id) }))]);
    } else {
      const map = new Map([["all",{ id:"all", name:"All Books", description:"Browse all books", color:"#e63946" }]]);
      products.forEach(p => { if (p?.category_id && !map.has(p.category_id)) map.set(p.category_id, { id:p.category_id, name:p.category_name||`Category ${p.category_id}`, description:'', color:getCategoryColor(p.category_id) }); });
      setCategories(Array.from(map.values()));
    }
  };

  const loadProductsFromBackend = async () => {
    setLoading(true);
    const d = await apiService.getAllProducts();
    if (d.success && Array.isArray(d.products)) {
      const enhanced = d.products.map(p => {
        const img = getPrimaryImage(p);
        const images = (p.images||[]).map(i => ({ ...i, image_url: getImageUrl(`/uploads/images/${i.image_name}`), thumbnail_url: getThumbnailUrl(i.thumbnail_name||`thumb_${i.image_name}`) }));
        return { ...p, image: img, thumbnail_url: img, images };
      });
      setProducts(enhanced);
      setTotalBooksCount(enhanced.length);
      setFeaturedProducts(enhanced.filter(p => p.featured===true||p.featured===1));
    } else { setProducts([]); setTotalBooksCount(0); setFeaturedProducts([]); }
    setLoading(false);
  };

  const fetchExchangeRates = async () => {
    const rates = await apiService.getExchangeRates();
    return rates || { INR:1, USD:0.012, EUR:0.011, GBP:0.0095, CAD:0.016, AUD:0.018, JPY:1.78, CNY:0.087, AED:0.044, SAR:0.045 };
  };

  const detectLocationByIP = async () => {
    try {
      setIsDetectingLocation(true);
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data && data.country_code) {
        return {
          country: data.country_name,
          countryCode: data.country_code,
          city: data.city,
          region: data.region,
          timezone: data.timezone,
          currency: data.currency || countryCurrencyMap[data.country_code] || 'USD',
          latitude: data.latitude,
          longitude: data.longitude
        };
      }
      return {
        country: "India",
        countryCode: "IN",
        city: "",
        region: "",
        timezone: "Asia/Kolkata",
        currency: "INR"
      };
    } catch (error) {
      console.error('Location detection failed:', error);
      return {
        country: "India",
        countryCode: "IN",
        city: "",
        region: "",
        timezone: "Asia/Kolkata",
        currency: "INR"
      };
    } finally {
      setIsDetectingLocation(false);
    }
  };

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const savedCart = localStorage.getItem('neoteeforge_cart');
    if (savedCart) { try { const p = JSON.parse(savedCart); setCartItems(Array.isArray(p)?p:[]); } catch{} }
    const savedWishlist = localStorage.getItem('neoteeforge_wishlist');
    if (savedWishlist) { try { const p = JSON.parse(savedWishlist); setWishlistItems(Array.isArray(p)?p:[]); } catch{} }

    const handleScroll = () => {
      setScrollPosition(window.scrollY);
      setHeaderCollapsed(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { localStorage.setItem('neoteeforge_cart', JSON.stringify(cartItems)); }, [cartItems]);
  useEffect(() => { localStorage.setItem('neoteeforge_wishlist', JSON.stringify(wishlistItems)); }, [wishlistItems]);
  useEffect(() => { if (auth?.token) loadUserOrders(); else setUserOrders([]); }, [auth]);
  useEffect(() => { window.scrollTo(0,0); }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (typingIndex < typingPhrases[currentPhrase].length) {
        setTypingText(typingPhrases[currentPhrase].substring(0, typingIndex+1));
        setTypingIndex(i => i+1);
      } else {
        setTimeout(() => { setTypingIndex(0); setCurrentPhrase(p => (p+1) % typingPhrases.length); }, 2000);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [typingIndex, currentPhrase]);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !statsAnimated) {
        setStatsAnimated(true);
        const target = { readers:10000, books:totalBooksCount||0, authors:Math.floor(totalBooksCount/2)||0, customers:99 };
        let step = 0;
        const go = () => { step++; const p = step/60; setAnimatedStats({ readers:Math.floor(target.readers*p), books:Math.floor(target.books*p), authors:Math.floor(target.authors*p), customers:Math.floor(target.customers*p) }); if (step<60) setTimeout(go,33); else setAnimatedStats(target); };
        go();
      }
    }, { threshold:0.5 });
    if (statsRef.current) observer.observe(statsRef.current);
    return () => { if (statsRef.current) observer.unobserve(statsRef.current); };
  }, [statsAnimated, totalBooksCount]);

  // Auto-detect location and set currency
  useEffect(() => {
    const initLocationAndCurrency = async () => {
      const savedCurrency = localStorage.getItem('neoteeforge_currency');
      const savedLocation = localStorage.getItem('neoteeforge_location');
      
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);
      
      if (savedCurrency) {
        setCurrency(savedCurrency);
      }
      
      if (savedLocation) {
        try { 
          const loc = JSON.parse(savedLocation);
          setUserLocation(loc);
          if (!savedCurrency && currencyData[loc.currency]) { 
            setCurrency(loc.currency); 
          }
        } catch(e) {
          console.error('Failed to parse saved location', e);
        }
      } else {
        try {
          setIsDetectingLocation(true);
          const loc = await detectLocationByIP();
          setUserLocation(loc);
          if (!savedCurrency && currencyData[loc.currency]) { 
            setCurrency(loc.currency); 
          }
          localStorage.setItem('neoteeforge_location', JSON.stringify(loc));
        } catch (error) {
          console.error('Location detection failed:', error);
          setUserLocation({
            country: "India",
            countryCode: "IN",
            city: "",
            region: "",
            timezone: "Asia/Kolkata",
            currency: "INR"
          });
        } finally {
          setIsDetectingLocation(false);
        }
      }
    };
    
    initLocationAndCurrency();
  }, []);

  useEffect(() => {
    const updateRates = async () => { 
      const r = await fetchExchangeRates(); 
      setExchangeRates(r); 
    };
    updateRates();
    localStorage.setItem('neoteeforge_currency', currency);
  }, [currency]);

  useEffect(() => { loadProductsFromBackend().then(loadCategoriesFromBackend); }, []);
  useEffect(() => { if (products.length>0 && categories.length===0) loadCategoriesFromBackend(); }, [products, categories.length]);

  // ── actions ────────────────────────────────────────────────────────────────
  const onLogin = (data) => {
    const userData = { ...data.user, role: data.user.role || (data.user.email==='fitrdeen@gmail.com'?'admin':'customer') };
    const authData = { token:data.token, user:userData };
    saveAuth(authData); setAuth(authData); setMobileMenuOpen(false); loadUserOrders();
  };
  const onLogout = () => { clearAuth(); setAuth(null); setUserOrders([]); setMobileMenuOpen(false); navigate('/'); };

  const addToCart = (product) => {
    if (!product?.id) return;
    if (product.stock_quantity<=0) { alert('Sorry, this product is out of stock!'); return; }
    setCartItems(prev => {
      const cart = Array.isArray(prev)?prev:[];
      const existing = cart.find(i=>i.id===product.id);
      if (existing) { if (existing.quantity+1>product.stock_quantity) { alert(`Only ${product.stock_quantity} items available!`); return cart; } return cart.map(i=>i.id===product.id?{...i,quantity:i.quantity+1}:i); }
      return [...cart,{...product,quantity:1}];
    });
  };

  const buyNow = (product) => {
    if (!product?.id) return;
    if (product.stock_quantity<=0) { alert('Sorry, this product is out of stock!'); return; }
    if (!auth) { alert('Please login to proceed with checkout'); navigate('/login'); return; }
    setCartItems([{...product,quantity:1}]); navigate('/checkout');
  };

  const removeFromCart = (id) => setCartItems(prev=>(Array.isArray(prev)?prev:[]).filter(i=>i.id!==id));
  const updateCartQuantity = (id, qty) => {
    if (qty<=0) { removeFromCart(id); return; }
    const p = products.find(p=>p.id===id);
    if (p&&qty>p.stock_quantity) { alert(`Only ${p.stock_quantity} items available!`); return; }
    setCartItems(prev=>(Array.isArray(prev)?prev:[]).map(i=>i.id===id?{...i,quantity:qty}:i));
  };

  const isInWishlist = (id) => wishlistItems.some(i=>i.id===id);
  const addToWishlist = (product) => {
    if (!product?.id) return;
    setWishlistItems(prev=>{ const wl=Array.isArray(prev)?prev:[]; if (wl.find(i=>i.id===product.id)) return wl; return [...wl,{...product,addedAt:new Date().toISOString()}]; });
    alert(`${product.title||product.name} added to wishlist!`);
  };
  const removeFromWishlist = (id) => setWishlistItems(prev=>(Array.isArray(prev)?prev:[]).filter(i=>i.id!==id));
  const toggleWishlist = (product) => { if (isInWishlist(product.id)) { removeFromWishlist(product.id); alert(`Removed from wishlist!`); } else addToWishlist(product); };

  const handleProductSelect = (product) => { if (!product?.id) return; navigate(`/product/${product.id}`); };
  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    const d = await apiService.searchProducts(q);
    if (d.success && Array.isArray(d.products)) setSearchResults(d.products);
    else setSearchResults(products.filter(p=>p&&(p.title||'').toLowerCase().includes(q.toLowerCase())||(p.author||'').toLowerCase().includes(q.toLowerCase())));
  };
  const handleCurrencyChange = (code) => { 
    setCurrency(code); 
    const info=currencyData[code]; 
    if(info) {
      const notification = document.createElement('div');
      notification.style.cssText = 'position:fixed;top:80px;right:20px;background:#059669;color:white;padding:12px 20px;border-radius:10px;z-index:9999;animation:slideIn 0.3s ease';
      notification.innerHTML = `🌍 Currency changed to ${info.name} (${info.symbol})`;
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  };
  const handleCheckoutClick = () => { if (!cartItems.length) { alert('Your cart is empty.'); return; } if (!auth) { alert('Please login first'); navigate('/login'); return; } navigate('/checkout'); };
  const handleNewsletterSubmit = (e) => { e.preventDefault(); const em=e.target.querySelector('input[type="email"]').value; apiService.subscribeNewsletter(em).then(r=>alert(r.success?'Thank you for subscribing!':'Subscription failed.')).catch(()=>alert('Thank you!')); e.target.reset(); };
  const navigateBack = () => navigate(-1);
  const navigateToHome = () => { navigate('/'); setMobileMenuOpen(false); window.scrollTo(0,0); };

  const openDonationModal = () => { setShowDonationModal(true); setDonationName(auth?.user?.name||""); setDonationEmail(auth?.user?.email||""); setDonationMessage(""); };
  const closeDonationModal = () => { setShowDonationModal(false); setDonationAmount(100); setCustomAmount(""); setDonationMessage(""); setIsProcessingDonation(false); };
  const handleDonationSubmit = async (e) => {
    e.preventDefault();
    if (!donationAmount||donationAmount<=0) { alert("Enter a valid amount."); return; }
    if (!donationName.trim()) { alert("Enter your name."); return; }
    if (!donationEmail.includes('@')) { alert("Enter a valid email."); return; }
    setIsProcessingDonation(true);
    const options = { key:"rzp_live_RzOl2N8ZMXkWYo", amount:donationAmount*100, currency:"INR", name:"NeoTeeForge Bookstore", description:"Support Children's Education", image:logo, handler:(r)=>{ alert(`Thank you! Payment ID: ${r.razorpay_payment_id}`); closeDonationModal(); }, prefill:{name:donationName,email:donationEmail}, theme:{color:"#e63946"} };
    try { const pay=new window.Razorpay(options); pay.open(); pay.on('payment.failed',()=>{ alert("Payment failed."); setIsProcessingDonation(false); }); } catch{ alert("Payment error. Try again."); setIsProcessingDonation(false); }
  };

  const filteredProducts = (() => {
    const base = searchQuery ? (Array.isArray(searchResults)?searchResults:[]) : (Array.isArray(products)?products:[]);
    return activeCategory==="all" ? base : base.filter(p=>p?.category_id?.toString()===activeCategory);
  })();

  const headerClass = `header ${scrollPosition>100?'scrolled':''} ${headerCollapsed?'collapsed':''}`;

  // Format description to handle checkmarks and bullets properly
  const formatDescription = (text) => {
    if (!text) return <p className="description-text">No description available.</p>;
    
    // Check if it's HTML
    if (typeof text === 'string' && (text.includes('<p>') || text.includes('<div>') || text.includes('<br') || text.includes('<h'))) {
      return <div className="description-html" dangerouslySetInnerHTML={{ __html: text }} />;
    }
    
    // Handle plain text with checkmarks and bullet points
    if (typeof text === 'string') {
      // Split into lines and process each line
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length > 1) {
        return (
          <div className="description-paragraphs">
            {lines.map((line, idx) => {
              const trimmed = line.trim();
              
              // Check for checkmark patterns (✓, ✔, ✅)
              if (trimmed.includes('✓') || trimmed.includes('✔') || trimmed.includes('✅')) {
                // Split the line by checkmarks if it contains multiple items
                const parts = trimmed.split(/[✓✔✅]/).filter(p => p.trim());
                
                if (parts.length > 1) {
                  // Multiple items in one line with checkmarks
                  return (
                    <div key={idx} className="description-check-group">
                      {parts.map((part, pIdx) => (
                        <div key={pIdx} className="description-check-item" style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          marginBottom: '8px'
                        }}>
                          <span style={{
                            color: '#059669',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            minWidth: '20px',
                            display: 'inline-block'
                          }}>✓</span>
                          <span style={{ color: '#475569', lineHeight: '1.5', flex: 1 }}>{part.trim()}</span>
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  // Single checkmark item
                  let content = trimmed.replace(/^[✓✔✅]\s*/, '');
                  return (
                    <div key={idx} className="description-check-item" style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        color: '#059669',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        minWidth: '20px',
                        display: 'inline-block'
                      }}>✓</span>
                      <span style={{ color: '#475569', lineHeight: '1.5', flex: 1 }}>{content}</span>
                    </div>
                  );
                }
              }
              // Check for bullet points (•, -, *)
              else if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
                let content = trimmed.substring(1).trim();
                return (
                  <div key={idx} className="description-bullet-item" style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    marginBottom: '6px'
                  }}>
                    <span style={{
                      color: '#e63946',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      minWidth: '15px'
                    }}>•</span>
                    <span style={{ color: '#475569', lineHeight: '1.5', flex: 1 }}>{content}</span>
                  </div>
                );
              }
              // Regular paragraph
              else {
                return <p key={idx} className="description-paragraph" style={{ marginBottom: '10px', color: '#475569' }}>{trimmed}</p>;
              }
            })}
          </div>
        );
      }
      
      // Single line - handle checkmarks if present
      if (text.includes('✓') || text.includes('✔') || text.includes('✅')) {
        // Split by checkmark if there are multiple items in one line
        const parts = text.split(/[✓✔✅]/).filter(p => p.trim());
        if (parts.length > 1) {
          return (
            <div className="description-check-items">
              {parts.map((part, idx) => (
                <div key={idx} className="description-check-item" style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    color: '#059669',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    minWidth: '20px',
                    display: 'inline-block'
                  }}>✓</span>
                  <span style={{ color: '#475569' }}>{part.trim()}</span>
                </div>
              ))}
            </div>
          );
        }
      }
      
      // Single line without special formatting
      return <p className="description-paragraph" style={{ color: '#475569' }}>{text}</p>;
    }
    
    return <p className="description-paragraph">No description available.</p>;
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="app">
      <link rel="icon" href={logo} type="image/jpeg" />
      <script src="https://checkout.razorpay.com/v1/checkout.js"></script>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }

        body {
          font-family: 'Montserrat', sans-serif;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
          color: #334155;
          overflow-x: hidden;
          min-height: 100vh;
          width: 100%;
        }

        .app {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          overflow: hidden;
          width: 100%;
        }

        /* Header */
        .header {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(25px);
          -webkit-backdrop-filter: blur(25px);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.8rem 1rem;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          gap: 0.5rem;
          flex-wrap: nowrap;
          width: 100%;
          max-width: 100vw;
          overflow: visible;
        }

        .header.collapsed {
          padding: 0.5rem 1rem;
        }

        .header.scrolled {
          box-shadow: 0 2px 40px rgba(0, 0, 0, 0.12);
        }

        .logo-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          flex-shrink: 0;
        }

        .logo-img {
          height: 34px;
          width: auto;
          object-fit: contain;
          border-radius: 6px;
          transition: all 0.3s ease;
        }

        .logo-fallback {
          width: 34px;
          height: 34px;
          background: linear-gradient(135deg, #e63946, #d00000);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 0.9rem;
        }

        .header.collapsed .logo-img,
        .header.collapsed .logo-fallback {
          height: 30px;
          width: 30px;
        }

        .logo-text {
          font-family: 'Playfair Display', serif;
          font-size: 1.1rem;
          font-weight: 700;
          background: linear-gradient(135deg, #e63946, #d00000);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: -0.5px;
          white-space: nowrap;
        }

        .nav-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
          justify-content: flex-end;
          min-width: 0;
          max-width: 100%;
        }

        .desktop-nav {
          display: none;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          justify-content: flex-end;
        }

        .mobile-nav-icons {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          flex-shrink: 0;
        }

        /* Search */
        .search-container {
          position: relative;
          display: flex;
          align-items: center;
          flex-shrink: 1;
          min-width: 0;
          flex: 1;
          max-width: 150px;
        }

        .search-input {
          padding: 0.5rem 0.7rem 0.5rem 2rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: rgba(255, 255, 255, 0.8);
          color: #334155;
          font-weight: 500;
          transition: all 0.3s;
          width: 100%;
          font-size: 0.8rem;
          flex: 1;
          min-width: 0;
        }

        .search-input:focus {
          outline: none;
          border-color: #e63946;
          box-shadow: 0 0 0 3px rgba(230, 57, 70, 0.1);
          background: rgba(255, 255, 255, 0.95);
        }

        .search-icon-wrap {
          position: absolute;
          left: 0.5rem;
          color: #64748b;
          width: 14px;
          height: 14px;
          pointer-events: none;
        }

        .search-results {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          z-index: 999999;
          margin-top: 5px;
          max-height: 400px;
          overflow-y: auto;
          width: 300px;
        }

        .search-result-item {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          padding: 0.8rem;
          cursor: pointer;
          transition: background-color 0.2s;
          border-bottom: 1px solid #f1f5f9;
        }

        .search-result-item:hover {
          background: #f8fafc;
        }

        .search-result-item:last-child {
          border-bottom: none;
        }

        .search-result-image {
          width: 40px;
          height: 56px;
          object-fit: cover;
          border-radius: 4px;
        }

        .search-result-info {
          flex: 1;
        }

        .search-result-name {
          font-weight: 600;
          font-size: 0.8rem;
          color: #1e293b;
          margin-bottom: 0.2rem;
        }

        .search-result-author {
          font-size: 0.7rem;
          color: #64748b;
        }

        .search-result-price {
          font-weight: 700;
          color: #e63946;
          font-size: 0.8rem;
        }

        /* Buttons */
        .btn {
          border: none;
          padding: 0.5rem 0.8rem;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-family: 'Montserrat', sans-serif;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          font-size: 0.8rem;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          white-space: nowrap;
        }

        .btn.primary {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          box-shadow: 0 4px 15px rgba(230, 57, 70, 0.3);
        }

        .btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(230, 57, 70, 0.4);
        }

        .btn.outline {
          border: 2px solid #e63946;
          background: transparent;
          color: #e63946;
        }

        .btn.outline:hover {
          background: #e63946;
          color: white;
          transform: translateY(-2px);
        }

        .header .btn.primary,
        .header .btn.outline {
          padding: 0.4rem 0.7rem;
          font-size: 0.75rem;
        }

        .btn.buy-now-hero {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          box-shadow: 0 6px 20px rgba(230, 57, 70, 0.4);
          padding: 0.7rem 1.2rem;
          font-size: 0.9rem;
          font-weight: 700;
        }

        .btn.buy-now-hero:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(230, 57, 70, 0.6);
        }

        /* Icon buttons */
        .icon-btn {
          position: relative;
          background: rgba(230, 57, 70, 0.1);
          border: none;
          padding: 0.5rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          flex-shrink: 0;
        }

        .icon-btn:hover {
          background: rgba(230, 57, 70, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(230, 57, 70, 0.2);
        }

        .cart-count,
        .wishlist-count,
        .orders-count {
          position: absolute;
          top: -6px;
          right: -6px;
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          font-size: 0.6rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          border: 2px solid white;
          animation: bounceIn 0.5s ease;
        }

        .orders-count {
          background: linear-gradient(135deg, #059669, #047857);
        }

        .desktop-icons {
          display: none;
          align-items: center;
          gap: 0.5rem;
          flex-shrink: 0;
        }

        .user-section {
          display: none;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        }

        .user-pill {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          padding: 0.3rem 0.5rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.7rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 110px;
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .admin-badge {
          background: rgba(255, 255, 255, 0.3);
          padding: 2px 4px;
          border-radius: 10px;
          font-size: 0.55rem;
          flex-shrink: 0;
        }

        .seller-badge {
          background: rgba(59, 130, 246, 0.3);
          padding: 2px 4px;
          border-radius: 10px;
          font-size: 0.55rem;
          flex-shrink: 0;
        }

        /* Hamburger menu */
        .hamburger-menu {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 22px;
          height: 16px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          z-index: 1001;
          flex-shrink: 0;
        }

        .hamburger-line {
          width: 100%;
          height: 2px;
          background: linear-gradient(135deg, #e63946, #d00000);
          border-radius: 2px;
          transition: all 0.3s;
          transform-origin: left;
        }

        .hamburger-menu.active .hamburger-line:nth-child(1) {
          transform: rotate(45deg);
        }

        .hamburger-menu.active .hamburger-line:nth-child(2) {
          opacity: 0;
        }

        .hamburger-menu.active .hamburger-line:nth-child(3) {
          transform: rotate(-45deg);
        }

        /* Mobile menu */
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(5px);
          z-index: 99998;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s;
        }

        .mobile-menu-overlay.active {
          opacity: 1;
          visibility: visible;
        }

        .mobile-menu {
          position: fixed;
          top: 0;
          right: -100%;
          width: 280px;
          height: 100vh;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1);
          z-index: 99999;
          transition: right 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          overflow-y: auto;
        }

        .mobile-menu.active {
          right: 0;
        }

        .mobile-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .mobile-menu-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.3rem;
          background: linear-gradient(135deg, #e63946, #d00000);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 700;
        }

        .mobile-nav-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .mobile-nav-item {
          padding: 0.8rem 1rem;
          border-radius: 10px;
          background: rgba(230, 57, 70, 0.05);
          color: #334155;
          font-weight: 500;
          transition: all 0.3s;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border: none;
          font-size: 0.9rem;
          font-family: 'Montserrat', sans-serif;
          width: 100%;
        }

        .mobile-nav-item:hover {
          background: rgba(230, 57, 70, 0.1);
          transform: translateX(5px);
        }

        .mobile-nav-item.active-nav {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          box-shadow: 0 4px 15px rgba(230, 57, 70, 0.3);
        }

        .mobile-user-section {
          margin-top: auto;
          padding-top: 1.5rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .mobile-user-info {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          padding: 0.8rem;
          background: rgba(230, 57, 70, 0.05);
          border-radius: 10px;
        }

        .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #e63946, #d00000);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
          flex-shrink: 0;
        }

        .user-details {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          font-weight: 600;
          color: #334155;
          font-size: 0.85rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .user-email {
          color: #64748b;
          font-size: 0.75rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* Hero section */
        .hero {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 90px 1rem 50px;
          background: radial-gradient(ellipse at 20% 50%, rgba(230, 57, 70, 0.15) 0%, transparent 50%),
                      radial-gradient(ellipse at 80% 20%, rgba(208, 0, 0, 0.12) 0%, transparent 50%),
                      radial-gradient(ellipse at 40% 80%, rgba(255, 107, 107, 0.1) 0%, transparent 50%),
                      linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
          position: relative;
          overflow: hidden;
          min-height: 85vh;
          width: 100%;
          text-align: center;
          z-index: 1;
          isolation: isolate;
        }

        .hero-content {
          flex: 1;
          max-width: 600px;
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
        }

        .hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.2rem, 5vw, 2rem);
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #334155, #e63946, #d00000);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1.2;
          text-align: center;
          min-height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 0 0.5rem;
        }

        .typing-cursor {
          display: inline-block;
          width: 3px;
          background: #e63946;
          margin-left: 4px;
          animation: blink 1s infinite;
        }

        .hero-subtitle {
          font-size: clamp(0.8rem, 3vw, 1rem);
          margin-bottom: 1.5rem;
          color: #64748b;
          line-height: 1.5;
          font-weight: 400;
          text-align: center;
          max-width: 500px;
          padding: 0 0.5rem;
        }

        .hero-buttons {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
          justify-content: center;
          width: 100%;
        }

        .hero-animation {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 2;
          width: 100%;
          margin-top: 2rem;
        }

        .animation-container {
          width: 100%;
          max-width: 300px;
          height: 250px;
          position: relative;
        }

        .book-animations {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .floating-book {
          position: absolute;
          width: clamp(40px, 10vw, 60px);
          height: clamp(60px, 15vw, 90px);
          border-radius: 6px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          animation: floatBook 6s ease-in-out infinite;
          background-size: cover;
          background-position: center;
          border: 2px solid white;
        }

        .book-1 {
          top: 15%;
          left: 5%;
          animation-delay: 0s;
          background-image: url('https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=150&h=200&fit=crop');
        }

        .book-2 {
          top: 55%;
          right: 10%;
          animation-delay: 1s;
          background-image: url('https://images.unsplash.com/photo-1558901357-ca41e027e43a?w=150&h=200&fit=crop');
        }

        .book-3 {
          bottom: 15%;
          left: 15%;
          animation-delay: 2s;
          background-image: url('https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=200&fit=crop');
        }

        .book-4 {
          top: 25%;
          right: 20%;
          animation-delay: 3s;
          background-image: url('https://images.unsplash.com/photo-1589829085413-56de8ae18c73?w=150&h=200&fit=crop');
        }

        .book-stack {
          position: absolute;
          bottom: 10%;
          right: 10%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .stack-book {
          width: clamp(35px, 8vw, 50px);
          height: clamp(50px, 12vw, 70px);
          border-radius: 5px;
          animation: stackPulse 4s ease-in-out infinite;
          background-size: cover;
          background-position: center;
          border: 2px solid white;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
        }

        .stack-book:nth-child(1) {
          animation-delay: 0s;
          background-image: url('https://images.unsplash.com/photo-1532012197267-da84d127e765?w=120&h=160&fit=crop');
        }

        .stack-book:nth-child(2) {
          animation-delay: 0.5s;
          background-image: url('https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=120&h=160&fit=crop');
        }

        .stack-book:nth-child(3) {
          animation-delay: 1s;
          background-image: url('https://images.unsplash.com/photo-1512820790803-83ca734da794?w=120&h=160&fit=crop');
        }

        /* Product grid - FULLY RESPONSIVE */
        .modern-products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem;
          width: 100%;
        }

        .modern-product-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          position: relative;
          border: 1px solid #f1f5f9;
          height: 100%;
          display: flex;
          flex-direction: column;
          cursor: pointer;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
        }

        .modern-product-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.12);
        }

        .product-image-container {
          position: relative;
          height: 280px;
          overflow: hidden;
          flex-shrink: 0;
          width: 100%;
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .modern-product-card:hover .product-image {
          transform: scale(1.05);
        }

        .product-badges {
          position: absolute;
          top: 10px;
          left: 10px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          z-index: 2;
        }

        .featured-badge {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .category-badge {
          background: rgba(255, 255, 255, 0.95);
          color: #334155;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 600;
          backdrop-filter: blur(10px);
        }

        .wishlist-icon {
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
          color: #64748b;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          z-index: 10;
        }

        .wishlist-icon:hover {
          color: #e63946;
          transform: scale(1.1);
        }

        .wishlist-icon.active {
          color: #e63946;
          background: rgba(230, 57, 70, 0.1);
        }

        .product-content {
          padding: 1.2rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .product-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.4rem;
          line-height: 1.3;
          cursor: pointer;
          transition: color 0.3s;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .product-title:hover {
          color: #e63946;
        }

        .product-author {
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 0.8rem;
        }

        .product-description {
          color: #64748b;
          font-size: 0.8rem;
          line-height: 1.4;
          margin-bottom: 0.8rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex: 1;
        }

        .product-price-section {
          margin-bottom: 1rem;
        }

        .price-row {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 0.4rem;
          flex-wrap: wrap;
        }

        .mrp-price {
          font-size: 0.9rem;
          color: #64748b;
          text-decoration: line-through;
        }

        .offer-price {
          font-size: 1.3rem;
          font-weight: 700;
          color: #e63946;
        }

        .discount-badge {
          background: linear-gradient(135deg, #059669, #047857);
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .final-price {
          font-size: 1.1rem;
          font-weight: 700;
          color: #334155;
          margin-top: 0.3rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .price-conversion {
          font-size: 0.65rem;
          color: #64748b;
          margin-top: 0.2rem;
        }

        .product-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.8rem;
        }

        .product-rating {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          color: #f59e0b;
          font-weight: 600;
          font-size: 0.8rem;
        }

        .product-rating span {
          color: #64748b;
          font-size: 0.7rem;
        }

        .product-details-list {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          margin-bottom: 1rem;
          padding: 0.8rem;
          background: #f8fafc;
          border-radius: 10px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          font-size: 0.7rem;
          color: #64748b;
        }

        .detail-label {
          font-weight: 600;
          color: #475569;
        }

        .product-actions {
          display: flex;
          gap: 0.6rem;
          z-index: 5;
          position: relative;
          margin-top: auto;
          flex-wrap: wrap;
        }

        .btn.add-to-cart {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          box-shadow: 0 4px 15px rgba(230, 57, 70, 0.3);
          padding: 0.5rem 0.8rem;
          font-weight: 600;
          flex: 1;
          border-radius: 10px;
          font-size: 0.8rem;
        }

        .btn.add-to-cart:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(230, 57, 70, 0.4);
        }

        .btn.buy-now {
          background: linear-gradient(135deg, #059669, #047857);
          color: white;
          box-shadow: 0 4px 15px rgba(5, 150, 105, 0.3);
          padding: 0.5rem 0.8rem;
          font-weight: 600;
          flex: 1;
          border-radius: 10px;
          font-size: 0.8rem;
        }

        .btn.buy-now:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(5, 150, 105, 0.4);
        }

        .stock-badge {
          display: inline-block;
          padding: 2px 6px;
          border-radius: 8px;
          font-size: 0.65rem;
          font-weight: 600;
        }

        .in-stock {
          background: #dcfce7;
          color: #166534;
        }

        .low-stock {
          background: #fef3c7;
          color: #92400e;
        }

        .out-of-stock {
          background: #fee2e2;
          color: #991b1b;
        }

        /* Sections - FULLY RESPONSIVE */
        .main-content {
          margin-top: 60px;
          flex: 1;
          width: 100%;
        }

        .page-content {
          padding: 1rem;
          min-height: calc(100vh - 60px);
          width: 100%;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          gap: 1rem;
          grid-column: 1 / -1;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #e63946;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-text {
          color: #64748b;
          font-size: 1rem;
        }

        .categories-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: white;
          width: 100%;
        }

        .section-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.4rem, 5vw, 2rem);
          background: linear-gradient(135deg, #334155, #e63946);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 2rem;
          position: relative;
          display: inline-block;
          text-align: center;
          width: 100%;
        }

        .section-title::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 50px;
          height: 3px;
          background: linear-gradient(135deg, #e63946, #d00000);
          border-radius: 2px;
        }

        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.2rem;
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem;
          width: 100%;
        }

        .category-card {
          background: white;
          border-radius: 16px;
          padding: 1.2rem;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          border: 1px solid #f1f5f9;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          cursor: pointer;
          width: 100%;
        }

        .category-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: var(--category-color, #e63946);
          transform: scaleX(0);
          transition: transform 0.4s ease;
        }

        .category-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
        }

        .category-card:hover::before,
        .category-card.active::before {
          transform: scaleX(1);
        }

        .category-card.active {
          transform: translateY(-5px);
          border-color: #e63946;
          box-shadow: 0 12px 25px rgba(230, 57, 70, 0.15);
        }

        .category-name {
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }

        .category-description {
          color: #64748b;
          margin-bottom: 1rem;
          line-height: 1.4;
          font-size: 0.85rem;
        }

        .products-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: #f8fafc;
          width: 100%;
        }

        .products-header {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1.5rem;
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
          padding: 0 1rem;
        }

        .products-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.4rem, 5vw, 2rem);
          background: linear-gradient(135deg, #334155, #e63946);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          text-align: center;
        }

        .category-filters {
          display: flex;
          gap: 0.6rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        .category-filter {
          padding: 0.5rem 0.8rem;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          cursor: pointer;
          transition: all 0.3s;
          font-weight: 500;
          font-size: 0.8rem;
          font-family: 'Montserrat', sans-serif;
        }

        .category-filter:hover {
          background: #f8fafc;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .category-filter.active {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          box-shadow: 0 6px 20px rgba(230, 57, 70, 0.3);
          border-color: transparent;
        }

        .featured-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: white;
          width: 100%;
        }

        .testimonials-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: #f8fafc;
          width: 100%;
        }

        .testimonial {
          background: white;
          border-left: 4px solid #e63946;
          padding: 1.2rem;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          position: relative;
          transition: transform 0.3s;
          font-size: 0.85rem;
        }

        .testimonial:hover {
          transform: translateY(-3px);
        }

        .testimonial::before {
          content: '"';
          font-size: 2.5rem;
          color: #e63946;
          position: absolute;
          top: -10px;
          left: 10px;
          opacity: 0.2;
          font-family: 'Playfair Display', serif;
        }

        .testimonial-author {
          margin-top: 1rem;
          font-weight: 600;
          color: #e63946;
          font-size: 0.8rem;
        }

        .community-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: linear-gradient(135deg, #1e293b, #334155);
          color: white;
          text-align: center;
          width: 100%;
        }

        .community-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .community-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.2rem;
          margin-top: 2rem;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          font-size: clamp(1.2rem, 4vw, 1.5rem);
          font-weight: 700;
          color: #e63946;
          margin-bottom: 0.4rem;
        }

        .stat-label {
          color: #cbd5e1;
          font-size: 0.8rem;
        }

        /* Donation section */
        .donation-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
          text-align: center;
          position: relative;
          overflow: hidden;
          width: 100%;
        }

        .donation-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
          padding: 0 1rem;
        }

        .donation-content {
          display: flex;
          flex-direction: column;
          gap: 2rem;
          align-items: center;
        }

        .donation-text {
          text-align: center;
          width: 100%;
        }

        .donation-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.4rem, 5vw, 1.6rem);
          margin-bottom: 1rem;
          line-height: 1.2;
        }

        .donation-description {
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
          opacity: 0.9;
          line-height: 1.5;
          text-align: justify;
        }

        .donation-form-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 1.2rem;
          border-radius: 16px;
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
          text-align: left;
          color: #334155;
          width: 100%;
          max-width: 400px;
        }

        .donation-form-title {
          font-size: 1.3rem;
          margin-bottom: 0.8rem;
          color: #059669;
          font-weight: 700;
          text-align: center;
        }

        .donation-amounts {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.6rem;
          margin-bottom: 1rem;
        }

        .donation-amount {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.6rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s;
          font-weight: 600;
          font-size: 0.85rem;
          font-family: 'Montserrat', sans-serif;
        }

        .donation-amount:hover {
          border-color: #059669;
          transform: translateY(-2px);
        }

        .donation-amount.selected {
          background: #059669;
          color: white;
          border-color: #059669;
        }

        .custom-amount {
          width: 100%;
          padding: 0.7rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.85rem;
          margin-bottom: 1rem;
          transition: border-color 0.3s;
          font-family: 'Montserrat', sans-serif;
        }

        .custom-amount:focus {
          outline: none;
          border-color: #059669;
          box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
        }

        .donation-details {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          margin-bottom: 1rem;
        }

        .donation-input {
          width: 100%;
          padding: 0.6rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.85rem;
          font-family: 'Montserrat', sans-serif;
        }

        .donation-input:focus {
          outline: none;
          border-color: #059669;
          box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
        }

        .donation-textarea {
          min-height: 70px;
          resize: vertical;
        }

        .donation-btn {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          border: none;
          padding: 0.7rem 1.2rem;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          box-shadow: 0 4px 15px rgba(230, 57, 70, 0.3);
          width: 100%;
          font-family: 'Montserrat', sans-serif;
        }

        .donation-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(230, 57, 70, 0.4);
        }

        .donation-note {
          text-align: center;
          margin-top: 0.8rem;
          font-size: 0.75rem;
          color: #64748b;
        }

        .donation-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(5px);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .donation-modal {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          max-width: 400px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .donation-modal-header {
          padding: 1.2rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .donation-modal-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.3rem;
          color: #059669;
          margin: 0;
        }

        .donation-modal-close {
          background: transparent;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.4rem;
          border-radius: 8px;
          transition: background-color 0.2s;
        }

        .donation-modal-close:hover {
          background: #f1f5f9;
        }

        .donation-modal-content {
          padding: 1.2rem;
        }

        /* Newsletter */
        .modern-newsletter {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: clamp(30px, 8vw, 70px) 1rem;
          position: relative;
          overflow: hidden;
          width: 100%;
        }

        .newsletter-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
          padding: 0 1rem;
        }

        .newsletter-content {
          display: flex;
          flex-direction: column;
          gap: 2rem;
          align-items: center;
        }

        .newsletter-text {
          color: white;
          text-align: center;
          width: 100%;
        }

        .newsletter-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.4rem, 5vw, 1.6rem);
          margin-bottom: 1rem;
          line-height: 1.2;
        }

        .gradient-text {
          background: linear-gradient(45deg, #fbbf24, #f59e0b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .newsletter-description {
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
          opacity: 0.9;
          line-height: 1.5;
          text-align: justify;
        }

        .newsletter-form-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 1.2rem;
          border-radius: 16px;
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 400px;
        }

        .newsletter-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .input-group {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 0.8rem;
          z-index: 2;
          color: #64748b;
          width: 18px;
          height: 18px;
        }

        .newsletter-input {
          width: 100%;
          padding: 0.7rem 0.7rem 0.7rem 2.5rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.85rem;
          transition: all 0.3s;
          background: white;
          font-family: 'Montserrat', sans-serif;
        }

        .newsletter-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .newsletter-btn {
          background: linear-gradient(135deg, #e63946, #d00000);
          color: white;
          border: none;
          padding: 0.7rem 1.2rem;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          box-shadow: 0 4px 15px rgba(230, 57, 70, 0.3);
          font-family: 'Montserrat', sans-serif;
        }

        .newsletter-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(230, 57, 70, 0.4);
        }

        .newsletter-note {
          text-align: center;
          margin-top: 0.8rem;
          font-size: 0.75rem;
          color: #64748b;
        }

        /* Footer */
        .footer {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          color: #334155;
          padding: 1.5rem 1rem 1rem;
          border-top: 1px solid #e2e8f0;
          width: 100%;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
          padding: 0 1rem;
        }

        .footer-logo {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #e63946, #d00000);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 0.8rem;
        }

        .footer-tagline {
          color: #64748b;
          font-size: 0.85rem;
          margin-bottom: 1.5rem;
          font-style: italic;
        }

        .social-links {
          display: flex;
          justify-content: center;
          gap: 0.8rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .social-link {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(230, 57, 70, 0.1);
          color: #e63946;
          transition: all 0.3s;
          text-decoration: none;
        }

        .social-link:hover {
          background: #e63946;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(230, 57, 70, 0.3);
        }

        .footer-links {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.8rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .footer-link {
          color: #64748b;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s;
          cursor: pointer;
          font-size: 0.8rem;
        }

        .footer-link:hover {
          color: #e63946;
        }

        .footer-bottom {
          color: #94a3b8;
          font-size: 0.75rem;
        }

        .footer-text {
          color: #64748b;
          font-size: 0.75rem;
          margin-bottom: 0.5rem;
        }

        /* Product detail page - FULLY RESPONSIVE */
        .product-detail-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem;
          background: white;
          width: 100%;
          min-height: calc(100vh - 60px);
        }

        .product-detail-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
          width: 100%;
        }

        .product-detail-images {
          position: relative;
          width: 100%;
          background: #f8fafc;
          border-radius: 12px;
          padding: 1rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
        }

        .product-main-image-container {
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          aspect-ratio: 210 / 297;
        }

        .product-main-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: white;
          padding: 1rem;
        }

        .product-thumbnails {
          display: flex;
          gap: 0.8rem;
          overflow-x: auto;
          padding: 1rem 0;
          width: 100%;
          margin-top: 1rem;
          -webkit-overflow-scrolling: touch;
        }

        .product-thumbnail {
          width: 80px;
          height: 113px;
          object-fit: cover;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s;
          border: 2px solid transparent;
          flex-shrink: 0;
          background: white;
          padding: 4px;
        }

        .product-thumbnail:hover,
        .product-thumbnail.active {
          border-color: #e63946;
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(230, 57, 70, 0.2);
        }

        .product-detail-info {
          padding: 1.5rem;
          width: 100%;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          border: 1px solid #e2e8f0;
        }

        .product-detail-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(1.4rem, 5vw, 1.8rem);
          color: #1e293b;
          margin-bottom: 0.8rem;
          line-height: 1.2;
          word-wrap: break-word;
        }

        .product-detail-author {
          color: #64748b;
          font-size: 1rem;
          margin-bottom: 1rem;
          font-weight: 500;
        }

        .product-detail-price-section {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }

        .detail-price-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.5rem;
          flex-wrap: wrap;
        }

        .detail-mrp {
          font-size: 1rem;
          color: #64748b;
          text-decoration: line-through;
        }

        .detail-offer-price {
          font-size: clamp(1.4rem, 5vw, 1.8rem);
          font-weight: 700;
          color: #e63946;
        }

        .detail-discount {
          background: linear-gradient(135deg, #059669, #047857);
          color: white;
          padding: 6px 12px;
          border-radius: 15px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .detail-final-price {
          font-size: clamp(1.2rem, 4vw, 1.5rem);
          font-weight: 700;
          color: #334155;
          margin-top: 0.8rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .converted-price {
          font-size: 1rem;
          color: #059669;
          font-weight: 600;
          background: rgba(5, 150, 105, 0.1);
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          white-space: nowrap;
        }

        .product-detail-description {
          color: #334155;
          line-height: 1.8;
          margin-bottom: 1.5rem;
          font-size: 1rem;
          text-align: left;
          word-wrap: break-word;
          font-family: 'Montserrat', sans-serif;
          font-weight: 400;
          letter-spacing: 0.3px;
          max-width: 100%;
          overflow-wrap: break-word;
        }

        .description-paragraph {
          margin-bottom: 1rem;
          color: #475569;
          font-size: 1rem;
          line-height: 1.6;
        }

        .description-check-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 8px;
          padding-left: 0;
        }

        .description-check-item .check-icon {
          color: #059669;
          font-weight: bold;
          font-size: 1.1rem;
          min-width: 20px;
          display: inline-block;
        }

        .description-check-item .check-content {
          color: #475569;
          line-height: 1.5;
          flex: 1;
        }

        .description-bullet-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 6px;
          padding-left: 0;
        }

        .description-bullet-item .bullet-icon {
          color: #e63946;
          font-weight: bold;
          font-size: 1rem;
          min-width: 15px;
        }

        .description-bullet-item .bullet-content {
          color: #475569;
          line-height: 1.5;
          flex: 1;
        }

        .description-html {
          margin-bottom: 1rem;
        }

        .description-html p {
          margin-bottom: 1rem;
          color: #475569;
        }

        .description-html strong {
          color: #1e293b;
        }

        .description-html em {
          color: #64748b;
        }

        .description-html ul,
        .description-html ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
          color: #475569;
        }

        .description-html li {
          margin-bottom: 0.3rem;
        }

        .description-html h1,
        .description-html h2,
        .description-html h3,
        .description-html h4 {
          margin-top: 1.5rem;
          margin-bottom: 0.8rem;
          color: #1e293b;
          font-weight: 600;
        }

        .product-detail-meta {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.2rem;
          margin-bottom: 2rem;
          padding: 1.2rem;
          background: #f8fafc;
          border-radius: 12px;
        }

        .meta-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .meta-label {
          font-weight: 600;
          color: #475569;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .meta-value {
          color: #334155;
          font-weight: 500;
          font-size: 0.95rem;
        }

        .stock-status {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .stock-in {
          background: #dcfce7;
          color: #166534;
        }

        .stock-low {
          background: #fef3c7;
          color: #92400e;
        }

        .stock-out {
          background: #fee2e2;
          color: #991b1b;
        }

        .product-detail-actions {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
          width: 100%;
          flex-wrap: wrap;
          position: sticky;
          bottom: 1rem;
          background: white;
          padding: 1rem;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          z-index: 10;
        }

        .back-button {
          background: transparent;
          border: 2px solid #e2e8f0;
          color: #334155;
          padding: 0.8rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          margin-bottom: 1rem;
          font-family: 'Montserrat', sans-serif;
        }

        .back-button:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        /* HOW IT WORKS SECTION - FIXED RESPONSIVE STYLES */
        .how-it-works-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: white;
          width: 100%;
        }

        .how-it-works-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          max-width: 900px;
          margin: 0 auto;
          padding: 1rem;
        }

        .how-it-works-card {
          background: white;
          border-radius: 16px;
          padding: 2rem 1.5rem;
          text-align: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          border: 1px solid #f1f5f9;
          transition: all 0.3s ease;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .how-it-works-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
        }

        .step-number {
          background: #e63946;
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          font-weight: bold;
          font-size: 1.5rem;
          box-shadow: 0 4px 10px rgba(230, 57, 70, 0.3);
        }

        .how-it-works-card h3 {
          margin-bottom: 1rem;
          color: #1e293b;
          font-size: 1.2rem;
          font-weight: 700;
        }

        .how-it-works-card p {
          color: #64748b;
          line-height: 1.6;
          font-size: 0.9rem;
          flex: 1;
        }

        /* WHY CHOOSE PRINTABLE LEARNING - FIXED CENTERED LAYOUT */
        .why-choose-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: #f8fafc;
          width: 100%;
        }

        .why-choose-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .benefits-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-width: 500px;
          margin: 0 auto;
        }

        .benefit-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.5rem;
          transition: transform 0.2s ease;
        }

        .benefit-item:hover {
          transform: translateX(5px);
        }

        .benefit-check {
          color: #059669;
          font-size: 1.5rem;
          font-weight: bold;
          min-width: 30px;
          text-align: center;
        }

        .benefit-text {
          color: #475569;
          font-size: 1rem;
          line-height: 1.5;
          font-weight: 500;
        }

        /* PARENTING GUIDES SECTION */
        .parenting-guides-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: white;
          width: 100%;
        }

        .guides-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          background: #f8fafc;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .guides-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-width: 500px;
          margin: 1.5rem auto;
        }

        .guide-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.5rem;
        }

        .guide-bullet {
          color: #e63946;
          font-size: 1.5rem;
          min-width: 30px;
          text-align: center;
        }

        .guide-text {
          color: #475569;
          font-size: 1rem;
          line-height: 1.5;
        }

        .guide-footer {
          text-align: center;
          font-weight: 600;
          color: #e63946;
          margin-top: 2rem;
          font-size: 1.1rem;
        }

        /* TRUST SECTION */
        .trust-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: linear-gradient(135deg, #f0f9ff 0%, #e6f2ff 100%);
          width: 100%;
        }

        .trust-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .trust-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-width: 500px;
          margin: 0 auto;
        }

        .trust-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.5rem;
        }

        .trust-check {
          color: #059669;
          font-size: 1.5rem;
          min-width: 30px;
          text-align: center;
        }

        .trust-text {
          color: #475569;
          font-size: 1rem;
          line-height: 1.5;
        }

        /* FAQ SECTION */
        .faq-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: white;
          width: 100%;
        }

        .faq-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          background: #f8fafc;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .faq-item {
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .faq-item:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .faq-question {
          color: #e63946;
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .faq-answer {
          color: #475569;
          line-height: 1.6;
        }

        /* PRINTING GUIDE SECTION */
        .printing-guide-section {
          padding: clamp(30px, 8vw, 70px) 1rem;
          background: linear-gradient(135deg, #f0f9ff 0%, #e6f2ff 100%);
          width: 100%;
        }

        .printing-guide-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .printing-options {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .printing-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.5rem;
        }

        .printing-tip {
          background: #f8fafc;
          padding: 1.5rem;
          border-radius: 12px;
          margin-top: 1.5rem;
          border-left: 4px solid #e63946;
        }

        .printing-tip strong {
          color: #e63946;
          display: block;
          margin-bottom: 0.5rem;
          font-size: 1rem;
        }

        .printing-tip p {
          color: #475569;
          line-height: 1.6;
        }

        /* Animations */
        @keyframes floatBook {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(5deg); }
        }

        @keyframes stackPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(180deg); }
          100% { transform: translateY(0) rotate(360deg); }
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slideOut {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }

        /* Responsive Breakpoints */
        @media (min-width: 768px) {
          .header {
            padding: 1rem 1.5rem;
          }
          .logo-text {
            font-size: 1.3rem;
          }
          .logo-img {
            height: 38px;
          }
          .search-container {
            max-width: 250px;
          }
          .desktop-nav {
            display: flex;
          }
          .desktop-icons {
            display: flex;
          }
          .user-section {
            display: flex;
          }
          .mobile-nav-icons {
            display: none;
          }
          .hamburger-menu {
            display: none;
          }
          .hero {
            flex-direction: row;
            text-align: left;
            padding: 110px 2rem 70px;
          }
          .hero-content {
            align-items: flex-start;
            text-align: left;
          }
          .hero-title {
            text-align: left;
          }
          .hero-subtitle {
            text-align: left;
          }
          .hero-buttons {
            flex-direction: row;
            justify-content: flex-start;
          }
          .hero-animation {
            margin-top: 0;
          }
          .animation-container {
            max-width: 400px;
            height: 350px;
          }
          .floating-book {
            width: 90px;
            height: 120px;
          }
          .stack-book {
            width: 60px;
            height: 80px;
          }
          .categories-section,
          .products-section,
          .featured-section,
          .testimonials-section,
          .community-section,
          .donation-section {
            padding: 70px 2rem;
          }
          .donation-content,
          .newsletter-content {
            flex-direction: row;
            gap: 3rem;
            text-align: left;
          }
          .donation-text,
          .newsletter-text {
            text-align: left;
          }
          .donation-description,
          .newsletter-description {
            text-align: left;
          }
          .product-detail-page {
            padding: 2rem;
            border-radius: 16px;
          }
          .product-detail-container {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }
          .product-main-image-container {
            max-width: 100%;
          }
          .product-thumbnail {
            width: 90px;
            height: 127px;
          }
          .product-detail-actions {
            flex-direction: row;
          }
          .community-stats {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .search-container {
            max-width: 300px;
          }
          .modern-products-grid {
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 2rem;
          }
          .product-detail-container {
            gap: 3rem;
          }
        }

        @media (max-width: 768px) {
          .how-it-works-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .how-it-works-card {
            padding: 1.5rem;
          }
          
          .step-number {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
            margin-bottom: 1rem;
          }
          
          .why-choose-container,
          .guides-container,
          .trust-container,
          .faq-container,
          .printing-guide-container {
            padding: 1.5rem;
          }
          
          .benefit-item,
          .guide-item,
          .trust-item,
          .printing-option {
            gap: 0.75rem;
          }
          
          .benefit-check,
          .guide-bullet,
          .trust-check {
            font-size: 1.3rem;
            min-width: 25px;
          }
        }

        @media (max-width: 640px) {
          .logo-text {
            display: none;
          }
          .search-container {
            max-width: 130px;
          }
          .product-actions {
            flex-direction: column;
          }
          .donation-amounts {
            grid-template-columns: repeat(2, 1fr);
          }
          .product-image-container {
            height: 220px;
          }
          .product-detail-actions {
            flex-direction: column;
          }
        }

        @media (max-width: 480px) {
          .hero {
            padding: 80px 1rem 40px;
            min-height: 80vh;
          }
          .categories-grid {
            grid-template-columns: 1fr;
          }
          .modern-products-grid {
            grid-template-columns: 1fr;
            padding: 0.5rem;
          }
          .icon-btn {
            width: 32px;
            height: 32px;
          }
          .mobile-menu {
            width: 100%;
          }
          .product-thumbnail {
            width: 60px;
            height: 85px;
          }
          .how-it-works-card {
            padding: 1.2rem;
          }
          .why-choose-container,
          .guides-container,
          .trust-container,
          .faq-container,
          .printing-guide-container {
            padding: 1rem;
          }
          .benefit-text,
          .guide-text,
          .trust-text,
          .faq-answer {
            font-size: 0.9rem;
          }
          .faq-question {
            font-size: 1rem;
          }
        }
      `}</style>

      {/* ── HEADER ── */}
      <header className={headerClass} ref={headerRef}>
        <div className="logo-container" onClick={navigateToHome}>
          {!logoError ? (
            <img src={logo} alt="NeoTeeForge" className="logo-img" onError={()=>setLogoError(true)} loading="eager"/>
          ) : (
            <div className="logo-fallback">NTF</div>
          )}
          <div className="logo-text">NeoTeeForge</div>
        </div>

        <nav className="nav-container">
          {/* Desktop Nav */}
          <div className="desktop-nav">
            {/* Search */}
            <div className="search-container">
              <div className="search-icon-wrap"><SearchIcon /></div>
              <input type="text" className="search-input" placeholder="Search books..." value={searchQuery} onChange={e=>handleSearch(e.target.value)}/>
              {searchQuery && searchResults.length>0 && (
                <div className="search-results">
                  {searchResults.map(p=>(
                    <div key={p.id} className="search-result-item" onClick={()=>{ handleProductSelect(p); setSearchQuery(''); setSearchResults([]); }}>
                      <img src={getPrimaryImage(p)} alt={p.title} className="search-result-image" onError={e=>{e.target.src='https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=100&h=150&fit=crop'}}/>
                      <div className="search-result-info">
                        <div className="search-result-name">{p.title||p.name}</div>
                        <div className="search-result-author">by {p.author}</div>
                      </div>
                      <div className="search-result-price">{convertPrice(p.price)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Currency Selector */}
            <CurrencySelector currency={currency} exchangeRates={exchangeRates} onCurrencyChange={handleCurrencyChange}/>

            {/* Desktop Icon Buttons */}
            <div className="desktop-icons">
              {auth && (
                <button className="icon-btn orders-btn" onClick={()=>navigate('/orders')} title="My Orders">
                  <OrdersIcon/>
                  {getOrdersCount()>0 && <span className="orders-count">{getOrdersCount()}</span>}
                </button>
              )}
              <button className="icon-btn wishlist-btn" onClick={()=>navigate('/wishlist')} title="Wishlist">
                <HeartIcon filled={wishlistItems.length>0}/>
                {wishlistItems.length>0 && <span className="wishlist-count">{wishlistItems.length}</span>}
              </button>
              <button className="icon-btn cart-btn" onClick={()=>navigate('/cart')} title="Cart">
                <CartIcon/>
                {getTotalCartItems()>0 && <span className="cart-count">{getTotalCartItems()}</span>}
              </button>
            </div>

            {/* User Section */}
            <div className="user-section">
              {auth ? (
                <>
                  <button className="btn outline" onClick={()=>navigate('/library')} style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <LibraryIcon/>My Library
                  </button>
                  <div className="user-pill">
                    <UserIcon/>{getUserDisplayName()}
                    {userIsAdmin && <span className="admin-badge">ADMIN</span>}
                    {auth?.user?.role==='seller' && <span className="seller-badge">SELLER</span>}
                  </div>
                  {userIsSeller && <button className="btn outline" onClick={()=>navigate('/seller/dashboard')} style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',color:'white',border:'none'}}><SellerIcon/>Seller</button>}
                  {userIsAdmin && <button className="btn primary" onClick={()=>navigate('/admin')}><CrownIcon/>Admin</button>}
                  <button className="btn outline" onClick={onLogout}>Logout</button>
                </>
              ) : (
                <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                  <button className="btn primary" onClick={()=>navigate('/signup')}>Sign up</button>
                  <button className="btn outline" onClick={()=>navigate('/login')}>Login</button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Nav Icons */}
          <div className="mobile-nav-icons">
            <div className="search-container">
              <div className="search-icon-wrap"><SearchIcon /></div>
              <input type="text" className="search-input" placeholder="Search..." value={searchQuery} onChange={e=>handleSearch(e.target.value)}/>
              {searchQuery && searchResults.length>0 && (
                <div className="search-results">
                  {searchResults.map(p=>(
                    <div key={p.id} className="search-result-item" onClick={()=>{ handleProductSelect(p); setSearchQuery(''); setSearchResults([]); setMobileMenuOpen(false); }}>
                      <img src={getPrimaryImage(p)} alt={p.title} className="search-result-image" onError={e=>{e.target.src='https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=100&h=150&fit=crop'}}/>
                      <div className="search-result-info">
                        <div className="search-result-name">{p.title||p.name}</div>
                        <div className="search-result-author">by {p.author}</div>
                      </div>
                      <div className="search-result-price">{convertPrice(p.price)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {auth && <button className="icon-btn" onClick={()=>{navigate('/orders');setMobileMenuOpen(false)}} title="Orders"><OrdersIcon/>{getOrdersCount()>0&&<span className="orders-count">{getOrdersCount()}</span>}</button>}
            <button className="icon-btn" onClick={()=>{navigate('/wishlist');setMobileMenuOpen(false)}}><HeartIcon filled={wishlistItems.length>0}/>{wishlistItems.length>0&&<span className="wishlist-count">{wishlistItems.length}</span>}</button>
            <button className="icon-btn" onClick={()=>{navigate('/cart');setMobileMenuOpen(false)}}><CartIcon/>{getTotalCartItems()>0&&<span className="cart-count">{getTotalCartItems()}</span>}</button>
            <button className={`hamburger-menu ${mobileMenuOpen?'active':''}`} onClick={()=>setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
              <span className="hamburger-line"></span><span className="hamburger-line"></span><span className="hamburger-line"></span>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen?'active':''}`} onClick={()=>setMobileMenuOpen(false)}/>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen?'active':''}`}>
        <div className="mobile-menu-header">
          <div className="mobile-menu-title">Menu</div>
          <button className="icon-btn" onClick={()=>setMobileMenuOpen(false)} style={{background:'rgba(230,57,70,0.1)'}}><CloseIcon/></button>
        </div>
        <div className="mobile-nav-items">
          {[
            {label:'Home',icon:<HomeIcon/>,view:'home',path:'/'},
            auth && {label:'My Library',icon:<LibraryIcon/>,view:'library',path:'/library'},
            auth && {label:`My Orders (${getOrdersCount()})`,icon:<OrdersIcon/>,view:'orders',path:'/orders'},
            {label:`Cart (${getTotalCartItems()})`,icon:<CartIcon/>,view:'cart',path:'/cart'},
            {label:`Wishlist (${wishlistItems.length})`,icon:<HeartIcon filled={wishlistItems.length>0}/>,view:'wishlist',path:'/wishlist'},
            {label:'Privacy Policy',icon:<PolicyIcon/>,view:'privacy-policy',path:'/privacy-policy'},
            {label:'Terms of Service',icon:<PolicyIcon/>,view:'terms-of-service',path:'/terms-of-service'},
            {label:'Shipping Policy',icon:<ShippingIcon/>,view:'shipping-policy',path:'/shipping-policy'},
            {label:'Return Policy',icon:<PolicyIcon/>,view:'return-policy',path:'/return-policy'},
            {label:'Contact Us',icon:<ContactIcon/>,view:'contact-us',path:'/contact-us'},
            {label:'About Us',icon:<InfoIcon/>,view:'about-us',path:'/about-us'},
            userIsSeller && {label:'Seller Dashboard',icon:<SellerIcon/>,view:'seller-dashboard',path:'/seller/dashboard'},
            userIsAdmin && {label:'Admin Dashboard',icon:<CrownIcon/>,view:'admin',path:'/admin'},
          ].filter(Boolean).map((item,i)=>(
            <button key={i} className={`mobile-nav-item ${currentView===item.view?'active-nav':''}`} onClick={()=>{navigate(item.path);setMobileMenuOpen(false)}}>
              {item.icon}{item.label}
            </button>
          ))}
          {/* Currency in mobile */}
          <div className="mobile-nav-item" style={{justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}><GlobeIcon/>Currency</div>
            <select value={currency} onChange={e=>{handleCurrencyChange(e.target.value);setMobileMenuOpen(false)}} style={{appearance:'none',background:'transparent',border:'none',color:'inherit',fontSize:'0.85rem',cursor:'pointer',fontFamily:'inherit',fontWeight:600}}>
              {Object.entries(currencyData).map(([code,info])=>(
                <option key={code} value={code}>{info.flag} {code} ({info.symbol})</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mobile-user-section">
          {auth ? (
            <>
              <div className="mobile-user-info">
                <div className="user-avatar">{(auth.user?.name||auth.user?.email||'U').charAt(0).toUpperCase()}</div>
                <div className="user-details">
                  <div className="user-name">{auth.user?.name||'Welcome back!'}{userIsAdmin?' (ADMIN)':''}{auth.user?.role==='seller'?' (SELLER)':''}</div>
                  <div className="user-email">{auth.user?.email||''}</div>
                </div>
              </div>
              {userIsAdmin && <button className="btn primary" onClick={()=>{navigate('/admin');setMobileMenuOpen(false)}} style={{width:'100%'}}><CrownIcon/>Admin Dashboard</button>}
              {userIsSeller && <button className="btn outline" onClick={()=>{navigate('/seller/dashboard');setMobileMenuOpen(false)}} style={{width:'100%',background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',color:'white',border:'none'}}><SellerIcon/>Seller Dashboard</button>}
              <button className="btn outline" onClick={onLogout} style={{width:'100%'}}>Logout</button>
            </>
          ) : (
            <>
              <button className="btn primary" onClick={()=>{navigate('/signup');setMobileMenuOpen(false)}} style={{width:'100%'}}>Sign up</button>
              <button className="btn outline" onClick={()=>{navigate('/login');setMobileMenuOpen(false)}} style={{width:'100%'}}>Login</button>
            </>
          )}
        </div>
      </div>

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="donation-modal-overlay">
          <div className="donation-modal">
            <div className="donation-modal-header">
              <h3 className="donation-modal-title">Support Our Mission</h3>
              <button className="donation-modal-close" onClick={closeDonationModal}><CloseIcon/></button>
            </div>
            <div className="donation-modal-content">
              <form onSubmit={handleDonationSubmit}>
                <div className="donation-form-title">Make a Donation</div>
                <div className="donation-amounts">
                  {[100,500,1000].map(amt=>(
                    <div key={amt} className={`donation-amount ${donationAmount===amt&&!customAmount?'selected':''}`} onClick={()=>{setDonationAmount(amt);setCustomAmount("")}}>₹{amt}</div>
                  ))}
                </div>
                <input type="number" className="custom-amount" placeholder="Custom amount (₹)" value={customAmount} onChange={e=>{setCustomAmount(e.target.value);if(e.target.value)setDonationAmount(parseInt(e.target.value)||0)}} min="1" max="100000"/>
                <div className="donation-details">
                  <input type="text" className="donation-input" placeholder="Your Name" value={donationName} onChange={e=>setDonationName(e.target.value)} required/>
                  <input type="email" className="donation-input" placeholder="Your Email" value={donationEmail} onChange={e=>setDonationEmail(e.target.value)} required/>
                  <textarea className="donation-input donation-textarea" placeholder="Optional message" value={donationMessage} onChange={e=>setDonationMessage(e.target.value)}/>
                </div>
                <button type="submit" className="donation-btn" disabled={isProcessingDonation}>
                  {isProcessingDonation ? 'Processing...' : <><DonationIcon/>Donate ₹{donationAmount}</>}
                </button>
                <p className="donation-note">Secure payment via RazorPay.</p>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <div className="main-content">
        <Routes>
          <Route path="/" element={
            <>
              {/* Hero Section with updated content */}
              <section className="hero" ref={heroRef}>
                <div className="hero-content">
                  <h1 className="hero-title">{typingText}<span className="typing-cursor"></span></h1>
                  <p className="hero-subtitle">High-quality printable workbooks designed to make learning fun and effective. Instant PDF download. No shipping. No waiting.</p>
                  <div className="hero-buttons">
                    <button className="btn buy-now-hero" onClick={()=>{const p=products[0];if(p)buyNow(p)}} disabled={products.length===0||loading}>
                      <RocketIcon/>{loading?'Loading...':'Start Learning Today'}
                    </button>
                    <button className="btn outline" onClick={()=>document.querySelector('.categories-section')?.scrollIntoView({behavior:'smooth'})}>
                      <BookIcon/>Explore Books
                    </button>
                  </div>
                </div>
                <div className="hero-animation">
                  <div className="animation-container">
                    <div className="book-animations">
                      <div className="floating-book book-1"/><div className="floating-book book-2"/>
                      <div className="floating-book book-3"/><div className="floating-book book-4"/>
                      <div className="book-stack"><div className="stack-book"/><div className="stack-book"/><div className="stack-book"/></div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Slide 2 - Download Once. Print Anytime. */}
              <section className="categories-section" style={{background:'linear-gradient(135deg, #f0f9ff 0%, #e6f2ff 100%)'}}>
                <h2 className="section-title">Download Once. Print Anytime.</h2>
                <div className="categories-grid" style={{gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))'}}>
                  <div className="category-card" style={{textAlign:'center',padding:'2rem'}}>
                    <div style={{fontSize:'3rem',marginBottom:'1rem'}}>🖨️</div>
                    <h3 style={{color:'#1e293b',marginBottom:'1rem'}}>Affordable Learning for Every Family</h3>
                    <p style={{color:'#475569',lineHeight:'1.6'}}>Print at home or at nearby copy centers. Use color for activity pages and black & white for reading pages to save costs.</p>
                    <button className="btn outline" onClick={()=>document.querySelector('.products-section')?.scrollIntoView({behavior:'smooth'})} style={{marginTop:'1.5rem'}}>
                      How It Works
                    </button>
                  </div>
                </div>
              </section>

              {/* Slide 3 - Guidance for Parents */}
              <section className="featured-section">
                <h2 className="section-title">Guidance for Parents. Growth for Children.</h2>
                <div className="categories-grid" style={{gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))'}}>
                  <div className="category-card" style={{textAlign:'center',padding:'2rem'}}>
                    <div style={{fontSize:'3rem',marginBottom:'1rem'}}>👪</div>
                    <h3 style={{color:'#1e293b',marginBottom:'1rem'}}>Practical Tools for Better Learning Habits</h3>
                    <p style={{color:'#475569',lineHeight:'1.6'}}>Discover simple, practical guides to help parents motivate, engage, and support their children in daily learning.</p>
                    <button className="btn outline" onClick={()=>navigate('/library')} style={{marginTop:'1.5rem'}}>
                      Explore Parenting Guides
                    </button>
                  </div>
                </div>
              </section>

              {/* SECTION 2 – HOW IT WORKS - FIXED RESPONSIVE */}
              <section className="how-it-works-section">
                <h2 className="section-title">How NeoTeeForge Works</h2>
                <div className="how-it-works-grid">
                  <div className="how-it-works-card">
                    <div className="step-number">1</div>
                    <h3>Choose Your Book</h3>
                    <p>Browse activity books and parenting guides designed for practical learning.</p>
                  </div>
                  <div className="how-it-works-card">
                    <div className="step-number">2</div>
                    <h3>Instant Download</h3>
                    <p>Complete your purchase and download the PDF file immediately.</p>
                  </div>
                  <div className="how-it-works-card">
                    <div className="step-number">3</div>
                    <h3>Print & Start Learning</h3>
                    <p>Print at home or at a local print shop and begin using the book right away.</p>
                  </div>
                </div>
              </section>

              {/* SECTION 3 – WHY PRINTABLE LEARNING? - FIXED CENTERED */}
              <section className="why-choose-section">
                <h2 className="section-title">Why Choose Printable Learning?</h2>
                <div className="why-choose-container">
                  <div className="benefits-list">
                    <div className="benefit-item">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">Instant access – no delivery delay</span>
                    </div>
                    <div className="benefit-item">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">Reusable – print as many times as needed</span>
                    </div>
                    <div className="benefit-item">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">Budget-friendly</span>
                    </div>
                    <div className="benefit-item">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">Learn at your own pace</span>
                    </div>
                    <div className="benefit-item">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">Perfect for home practice and revision</span>
                    </div>
                  </div>
                </div>
              </section>

              {loading && <section className="products-section"><div className="loading-spinner"><div className="spinner"/><p className="loading-text">Loading books from backend...</p></div></section>}

              {/* Categories */}
              {!loading && categories.length>0 && (
                <section className="categories-section">
                  <h2 className="section-title">Explore Categories</h2>
                  <div className="categories-grid">
                    {categories.map(cat=>(
                      <div key={cat.id} className={`category-card ${activeCategory===cat.id.toString()?'active':''}`} onClick={()=>{setActiveCategory(cat.id.toString());document.querySelector('.products-section')?.scrollIntoView({behavior:'smooth'})}} style={{'--category-color':cat.color}}>
                        <BookIcon className="category-icon"/>
                        <h3 className="category-name">{cat.name}</h3>
                        <p className="category-description">{cat.description}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Products - FULLY RESPONSIVE GRID */}
              {!loading && (
                <section className="products-section">
                  <div className="products-header">
                    <h2 className="products-title">
                      {activeCategory==="all"?"All Books":(categories.find(c=>c.id.toString()===activeCategory)?.name||"")+" Books"}
                      <div style={{fontSize:'0.8rem',color:'#64748b',marginTop:'0.5rem'}}>
                        Showing {filteredProducts.length} of {totalBooksCount} books • Prices in {currencyData[currency]?.name} ({currencyData[currency]?.symbol})
                      </div>
                    </h2>
                    {categories.length>0 && (
                      <div className="category-filters">
                        <button className={`category-filter ${activeCategory==="all"?"active":""}`} onClick={()=>setActiveCategory("all")}>All Books</button>
                        {categories.filter(c=>c.id!=='all').map(cat=>(
                          <button key={cat.id} className={`category-filter ${activeCategory===cat.id.toString()?"active":""}`} onClick={()=>setActiveCategory(cat.id.toString())}>{cat.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {filteredProducts.length===0 ? (
                    <div style={{textAlign:'center',padding:'2rem',color:'#64748b'}}><h3>No products found</h3><p>Try selecting a different category.</p></div>
                  ) : (
                    <div className="modern-products-grid">
                      {filteredProducts.map(product=>{
                        const pb = getPriceBreakdown(product.price, product);
                        const img = getPrimaryImage(product);
                        return (
                          <div key={product.id} className="modern-product-card" onClick={e=>{if(!e.target.closest('.product-actions')&&!e.target.closest('.wishlist-icon'))handleProductSelect(product)}}>
                            <div className="product-image-container">
                              <img src={img} alt={product.title} className="product-image" onError={e=>{e.target.src='https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=600&fit=crop'}}/>
                              <div className="product-badges">
                                {product.featured&&<div className="featured-badge">Featured</div>}
                                <div className="category-badge">{getCategoryName(product.category_id)}</div>
                              </div>
                              <button className={`wishlist-icon ${isInWishlist(product.id)?'active':''}`} onClick={e=>{e.stopPropagation();toggleWishlist(product)}}><HeartIcon filled={isInWishlist(product.id)}/></button>
                            </div>
                            <div className="product-content">
                              <h3 className="product-title" onClick={e=>{e.stopPropagation();handleProductSelect(product)}}>{product.title||product.name}</h3>
                              <p className="product-author">by {product.author}</p>
                              <p className="product-description">{product.description}</p>
                              <div className="product-price-section">
                                <div className="price-row">
                                  <span className="mrp-price">{convertPrice(pb.mrp)}</span>
                                  <span className="offer-price">{convertPrice(pb.offerPrice)}</span>
                                  <span className="discount-badge">{pb.discountPercentage}% OFF</span>
                                </div>
                                <div className="final-price">
                                  <span>Total: {convertPrice(pb.totalPrice)}</span>
                                  <span className="price-conversion">(₹{pb.totalPrice} INR)</span>
                                </div>
                              </div>
                              <div className="product-meta">
                                <div className="product-rating">⭐ {product.rating||'4.0'}<span>({(product.id*17%900)+100} reviews)</span></div>
                              </div>
                              <div className="product-details-list">
                                <div className="detail-item"><span className="detail-label">Stock:</span><span className={`stock-badge ${product.stock_quantity>10?'in-stock':product.stock_quantity>0?'low-stock':'out-of-stock'}`}>{product.stock_quantity>10?'In Stock':product.stock_quantity>0?'Low Stock':'Out of Stock'}</span></div>
                                <div className="detail-item"><span className="detail-label">Category:</span><span>{getCategoryName(product.category_id)}</span></div>
                                {product.allow_download&&<div className="detail-item"><span className="detail-label">Features:</span><span>📥 PDF Download</span></div>}
                              </div>
                              <div className="product-actions">
                                <button className="btn add-to-cart" onClick={e=>{e.stopPropagation();addToCart(product)}} disabled={product.stock_quantity<=0}>Add to Cart</button>
                                <button className="btn buy-now" onClick={e=>{e.stopPropagation();buyNow(product)}} disabled={product.stock_quantity<=0}>Buy Now</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {/* SECTION 4 – PRINTING GUIDE - FIXED */}
              <section className="printing-guide-section">
                <h2 className="section-title">Easy & Affordable Printing Options</h2>
                <div className="printing-guide-container">
                  <p style={{marginBottom:'1.5rem',color:'#475569'}}>After purchase, you will receive a downloadable PDF file. You can print your book:</p>
                  <div className="printing-options">
                    <div className="printing-option">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">Using your home printer</span>
                    </div>
                    <div className="printing-option">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">At local copy or print shops</span>
                    </div>
                    <div className="printing-option">
                      <span className="benefit-check">✓</span>
                      <span className="benefit-text">At digital printing centers in your area</span>
                    </div>
                  </div>
                  <p style={{color:'#475569',marginBottom:'1.5rem'}}>Most cities worldwide offer affordable printing services.</p>
                  <div className="printing-tip">
                    <strong>To Save Money:</strong>
                    <p>We recommend printing colorful activity pages in color and printing reading or writing pages in black & white. This helps reduce printing costs while maintaining a great learning experience.</p>
                  </div>
                </div>
              </section>

              {/* SECTION 5 – FOR PARENTS - FIXED */}
              <section className="parenting-guides-section">
                <h2 className="section-title">Support Your Child's Learning Journey</h2>
                <div className="guides-container">
                  <p style={{marginBottom:'1.5rem',color:'#475569',textAlign:'center'}}>NeoTeeForge also offers practical guides for parents on:</p>
                  <div className="guides-list">
                    <div className="guide-item">
                      <span className="guide-bullet">•</span>
                      <span className="guide-text">Building study habits</span>
                    </div>
                    <div className="guide-item">
                      <span className="guide-bullet">•</span>
                      <span className="guide-text">Encouraging focus</span>
                    </div>
                    <div className="guide-item">
                      <span className="guide-bullet">•</span>
                      <span className="guide-text">Managing screen time</span>
                    </div>
                    <div className="guide-item">
                      <span className="guide-bullet">•</span>
                      <span className="guide-text">Creating productive routines</span>
                    </div>
                    <div className="guide-item">
                      <span className="guide-bullet">•</span>
                      <span className="guide-text">Motivating children positively</span>
                    </div>
                  </div>
                  <div className="guide-footer">
                    Simple advice. Practical solutions. Real impact.
                  </div>
                </div>
              </section>

              {/* SECTION 6 – TRUST SECTION - FIXED */}
              <section className="trust-section">
                <h2 className="section-title">Why Parents Trust NeoTeeForge</h2>
                <div className="trust-container">
                  <div className="trust-list">
                    <div className="trust-item">
                      <span className="trust-check">✓</span>
                      <span className="trust-text">Instant secure downloads</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-check">✓</span>
                      <span className="trust-text">Designed for home learning</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-check">✓</span>
                      <span className="trust-text">Clear instructions included</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-check">✓</span>
                      <span className="trust-text">Suitable for multiple languages (expanding soon)</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-check">✓</span>
                      <span className="trust-text">Constantly growing collection</span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Featured */}
              {!loading && featuredProducts.length>0 && (
                <section className="featured-section">
                  <h2 className="section-title">Featured Books</h2>
                  <div className="modern-products-grid">
                    {featuredProducts.map(product=>{
                      const pb = getPriceBreakdown(product.price,product);
                      const img = getPrimaryImage(product);
                      return (
                        <div key={product.id} className="modern-product-card" onClick={e=>{if(!e.target.closest('.product-actions')&&!e.target.closest('.wishlist-icon'))handleProductSelect(product)}}>
                          <div className="product-image-container">
                            <img src={img} alt={product.title} className="product-image" onError={e=>{e.target.src='https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=600&fit=crop'}}/>
                            <div className="product-badges"><div className="featured-badge">Featured</div><div className="category-badge">{getCategoryName(product.category_id)}</div></div>
                            <button className={`wishlist-icon ${isInWishlist(product.id)?'active':''}`} onClick={e=>{e.stopPropagation();toggleWishlist(product)}}><HeartIcon filled={isInWishlist(product.id)}/></button>
                          </div>
                          <div className="product-content">
                            <h3 className="product-title" onClick={e=>{e.stopPropagation();handleProductSelect(product)}}>{product.title||product.name}</h3>
                            <p className="product-author">by {product.author}</p>
                            <p className="product-description">{product.description}</p>
                            <div className="product-price-section">
                              <div className="price-row">
                                <span className="mrp-price">{convertPrice(pb.mrp)}</span>
                                <span className="offer-price">{convertPrice(pb.offerPrice)}</span>
                                <span className="discount-badge">{pb.discountPercentage}% OFF</span>
                              </div>
                              <div className="final-price">
                                <span>Total: {convertPrice(pb.totalPrice)}</span>
                                <span className="price-conversion">(₹{pb.totalPrice} INR)</span>
                              </div>
                            </div>
                            <div className="product-actions">
                              <button className="btn add-to-cart" onClick={e=>{e.stopPropagation();addToCart(product)}} disabled={product.stock_quantity<=0}>Add to Cart</button>
                              <button className="btn buy-now" onClick={e=>{e.stopPropagation();buyNow(product)}} disabled={product.stock_quantity<=0}>Buy Now</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* FAQ SECTION - FIXED */}
              <section className="faq-section">
                <h2 className="section-title">Frequently Asked Questions</h2>
                <div className="faq-container">
                  <div className="faq-item">
                    <h3 className="faq-question">1. Are these physical books?</h3>
                    <p className="faq-answer">No. All products are digital PDF downloads.</p>
                  </div>
                  <div className="faq-item">
                    <h3 className="faq-question">2. When will I receive my book?</h3>
                    <p className="faq-answer">Immediately after successful payment.</p>
                  </div>
                  <div className="faq-item">
                    <h3 className="faq-question">3. Can I print multiple times?</h3>
                    <p className="faq-answer">Yes. You can print the file as many times as needed for personal use.</p>
                  </div>
                  <div className="faq-item">
                    <h3 className="faq-question">4. Where can I print if I don't have a printer?</h3>
                    <p className="faq-answer">You can print at nearby copy centers or local print shops.</p>
                  </div>
                  <div className="faq-item">
                    <h3 className="faq-question">5. Are these books suitable worldwide?</h3>
                    <p className="faq-answer">Yes. Our books are designed to be simple, practical, and globally usable.</p>
                  </div>
                </div>
              </section>

              {/* Donation */}
              <section className="donation-section">
                <div className="donation-container">
                  <div className="donation-content">
                    <div className="donation-text">
                      <h2 className="donation-title">Support Children's Education</h2>
                      <p className="donation-description">We're dedicated to providing digital products that help children with education and self-development. With the revenue we earn, we're continually working to improve our website and offer even better resources for kids.<br/><br/>Your generous donations will directly support this mission. Every contribution helps us create and share more high-quality digital books and resources that can enrich children's lives. Thank you for supporting us in making a difference!</p>
                    </div>
                    <div className="donation-form-container">
                      <h3 className="donation-form-title">Make a Difference Today</h3>
                      <div className="donation-amounts">
                        {[100,500,1000].map(amt=>(
                          <div key={amt} className={`donation-amount ${donationAmount===amt&&!customAmount?'selected':''}`} onClick={()=>{setDonationAmount(amt);setCustomAmount("")}}>₹{amt}</div>
                        ))}
                      </div>
                      <input type="number" className="custom-amount" placeholder="Custom amount (₹)" value={customAmount} onChange={e=>{setCustomAmount(e.target.value);if(e.target.value)setDonationAmount(parseInt(e.target.value)||0)}} min="1" max="100000"/>
                      <button className="donation-btn" onClick={openDonationModal}><DonationIcon/>Donate ₹{donationAmount}</button>
                      <p className="donation-note">Secure payment via RazorPay. Every rupee makes a difference!</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Stats */}
              <section className="community-section" ref={statsRef}>
                <div className="community-content">
                  <h2 className="section-title" style={{color:'white',WebkitTextFillColor:'white'}}>Join Our Learning Community</h2>
                  <div className="community-stats">
                    <div className="stat-item"><div className="stat-number">{animatedStats.readers.toLocaleString()}+</div><div className="stat-label">Happy Families</div></div>
                    <div className="stat-item"><div className="stat-number">{animatedStats.books.toLocaleString()}+</div><div className="stat-label">Printable Books</div></div>
                    <div className="stat-item"><div className="stat-number">{animatedStats.authors}+</div><div className="stat-label">Learning Guides</div></div>
                    <div className="stat-item"><div className="stat-number">{animatedStats.customers}%</div><div className="stat-label">Satisfied Parents</div></div>
                  </div>
                </div>
              </section>

              {/* Testimonials */}
              <section className="testimonials-section">
                <h2 className="section-title">What Parents Say</h2>
                <div className="modern-products-grid">
                  <div className="testimonial"><p>"These printable activity books have been a game-changer for our homeschool routine. My kids love the colorful pages and I love that I can print them again for my younger child!"</p><p className="testimonial-author">— Sarah Johnson, Mom of 3</p></div>
                  <div className="testimonial"><p>"The parenting guides helped us establish a consistent learning routine. Screen time management tips were especially helpful. Highly recommend!"</p><p className="testimonial-author">— Michael Chen, Dad</p></div>
                </div>
              </section>

              {/* Newsletter */}
              <section className="modern-newsletter">
                <div className="newsletter-container">
                  <div className="newsletter-content">
                    <div className="newsletter-text">
                      <h2 className="newsletter-title">Stay in the <span className="gradient-text">Learning Loop</span></h2>
                      <p className="newsletter-description">Get exclusive access to new printable books, parenting tips, special discounts, and educational insights delivered directly to your inbox.</p>
                    </div>
                    <div className="newsletter-form-container">
                      <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                        <div className="input-group">
                          <div className="input-icon"><UserIcon/></div>
                          <input type="text" className="newsletter-input" placeholder="Your full name" required/>
                        </div>
                        <div className="input-group">
                          <div className="input-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4ZM22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                          <input type="email" className="newsletter-input" placeholder="Your email address" required/>
                        </div>
                        <button type="submit" className="newsletter-btn">Subscribe Now
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </button>
                      </form>
                      <p className="newsletter-note">No spam ever. Unsubscribe at any time.</p>
                    </div>
                  </div>
                </div>
                <div style={{textAlign:'center',marginTop:'1rem',color:'white',fontSize:'0.8rem',opacity:0.8}}>
                  More regional language editions coming soon.
                </div>
              </section>
            </>
          }/>

          <Route path="/product/:id" element={<ProductDetailWrapper 
            products={products} 
            currency={currency} 
            exchangeRates={exchangeRates} 
            onBack={navigateBack} 
            onAddToCart={addToCart} 
            onBuyNow={buyNow} 
            onToggleWishlist={toggleWishlist} 
            isInWishlist={isInWishlist} 
            convertPrice={convertPrice} 
            getPriceBreakdown={getPriceBreakdown} 
            getCategoryName={getCategoryName} 
            getImageUrl={getImageUrl} 
            getThumbnailUrl={getThumbnailUrl} 
            getPrimaryImage={getPrimaryImage}
            formatDescription={formatDescription}
          />}/>

          {/* Other routes */}
          <Route path="/signup" element={<div className="page-content"><Signup onSuccess={d=>{onLogin(d);navigate('/')}} onSwitchToLogin={()=>navigate('/login')}/></div>}/>
          <Route path="/login" element={<div className="page-content"><Login onSuccess={d=>{onLogin(d);navigate('/')}} onSwitchToSignup={()=>navigate('/signup')}/></div>}/>
          <Route path="/admin" element={auth&&userIsAdmin?<div className="page-content"><AdminDashboard auth={auth} onBack={navigateBack}/></div>:<div className="page-content"><h2>Access Denied</h2><button onClick={()=>navigate('/')}>Back to Home</button></div>}/>
          <Route path="/seller/dashboard" element={userIsSeller?<div className="page-content"><SellerDashboard/></div>:<div className="page-content"><h2>Access Denied</h2><button onClick={()=>navigate('/')}>Back to Home</button></div>}/>
          <Route path="/cart" element={<div className="page-content"><CartPage auth={auth} cartItems={cartItems} onUpdateQuantity={updateCartQuantity} onRemoveItem={removeFromCart} currency={currency} currencyData={currencyData} exchangeRates={exchangeRates} convertPrice={convertPrice} getPriceBreakdown={getPriceBreakdown} onCheckout={handleCheckoutClick} getPrimaryImage={getPrimaryImage}/></div>}/>
          <Route path="/checkout" element={<div className="page-content"><Checkout auth={auth} cartItems={cartItems} currency={currency} currencyData={currencyData} exchangeRates={exchangeRates} convertPrice={convertPrice} getPriceBreakdown={getPriceBreakdown} userLocation={userLocation} onBack={()=>navigate('/cart')} onOrderSuccess={orderData=>{setCartItems([]);loadUserOrders();navigate('/orders');alert(`Order placed! ID: ${orderData.id}`)}} navigate={navigate}/></div>}/>
          <Route path="/wishlist" element={<div className="page-content"><WishlistPage wishlistItems={wishlistItems} onRemoveFromWishlist={removeFromWishlist} onAddToCart={addToCart} onBuyNow={buyNow} onProductSelect={handleProductSelect} currency={currency} currencyData={currencyData} exchangeRates={exchangeRates} convertPrice={convertPrice} getPriceBreakdown={getPriceBreakdown} onBack={()=>navigate('/')} getPrimaryImage={getPrimaryImage}/></div>}/>
          <Route path="/orders" element={<div className="page-content"><OrdersPage orders={userOrders} currency={currency} currencyData={currencyData} exchangeRates={exchangeRates} convertPrice={convertPrice} getPriceBreakdown={getPriceBreakdown} onBack={()=>navigate('/')} auth={auth}/></div>}/>
          <Route path="/library" element={<div className="page-content"><BookLibrary auth={auth} onBack={()=>navigate('/')} wishlistItems={wishlistItems} onRemoveFromWishlist={removeFromWishlist} onAddToCart={addToCart} onBuyNow={buyNow} onProductSelect={handleProductSelect} currency={currency} currencyData={currencyData} exchangeRates={exchangeRates} convertPrice={convertPrice} getPriceBreakdown={getPriceBreakdown} getPrimaryImage={getPrimaryImage}/></div>}/>
          <Route path="/privacy-policy" element={<div className="page-content"><PrivacyPolicy/></div>}/>
          <Route path="/terms-of-service" element={<div className="page-content"><TermsOfService/></div>}/>
          <Route path="/shipping-policy" element={<div className="page-content"><ShippingPolicy/></div>}/>
          <Route path="/return-policy" element={<div className="page-content"><ReturnPolicy/></div>}/>
          <Route path="/contact-us" element={<div className="page-content"><ContactUs/></div>}/>
          <Route path="/about-us" element={<div className="page-content"><AboutUs/></div>}/>
        </Routes>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">NeoTeeForge</div>
          <p className="footer-tagline">Printable activity books for smart kids • Instant PDF downloads</p>
          <div className="social-links">
            <a href="#" className="social-link" aria-label="Facebook"><FacebookIcon/></a>
            <a href="#" className="social-link" aria-label="Twitter"><TwitterIcon/></a>
            <a href="#" className="social-link" aria-label="Instagram"><InstagramIcon/></a>
            <a href="#" className="social-link" aria-label="LinkedIn"><LinkedInIcon/></a>
          </div>
          <div className="footer-links">
            {[['Privacy Policy','/privacy-policy'],['Terms of Service','/terms-of-service'],['Shipping Policy','/shipping-policy'],['Return Policy','/return-policy'],['Contact Us','/contact-us'],['About Us','/about-us']].map(([label,path])=>(
              <span key={path} className="footer-link" onClick={()=>navigate(path)}>{label}</span>
            ))}
          </div>
          <div className="footer-bottom">
            <p className="footer-text">© 2025 NeoTeeForge — Printable Learning Resources for Kids</p>
            <p className="footer-text">Built with ❤️ for parents and educators</p>
            <p className="footer-text" style={{fontSize:'0.72rem',color:'#94a3b8',marginTop:'0.5rem'}}>Total Books: {totalBooksCount} | Prices in {currencyData[currency]?.name}. Exchange rates from backend.</p>
            <p className="footer-text" style={{fontSize:'0.7rem',color:'#94a3b8'}}>More regional language editions coming soon.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── ProductDetailWrapper - UPDATED with printable books content ───
function ProductDetailWrapper({ products, currency, exchangeRates, onBack, onAddToCart, onBuyNow, onToggleWishlist, isInWishlist, convertPrice, getPriceBreakdown, getCategoryName, getImageUrl, getThumbnailUrl, getPrimaryImage, formatDescription }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');

  useEffect(() => {
    window.scrollTo(0, 0);
    const fromList = products.find(p => p.id === parseInt(id));
    if (fromList) {
      setProduct(fromList);
      setLoading(false);
    } else {
      apiService.getProduct(id).then(d => {
        if (d.success && d.product) {
          const p = d.product;
          const images = (p.images||[]).map(img => ({ ...img, image_url: getImageUrl(`/uploads/images/${img.image_name}`), thumbnail_url: getThumbnailUrl(img.thumbnail_name||`thumb_${img.image_name}`) }));
          setProduct({ ...p, image: getPrimaryImage(p), images });
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id, products]);

  if (loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh'}}><div style={{width:'40px',height:'40px',border:'4px solid #f3f3f3',borderTop:'4px solid #e63946',borderRadius:'50%',animation:'spin 1s linear infinite'}}/></div>;
  if (!product) return <div style={{textAlign:'center',padding:'3rem'}}><h2>Product not found</h2><button onClick={()=>navigate('/')} style={{marginTop:'1rem',padding:'0.8rem 1.5rem',background:'#e63946',color:'white',border:'none',borderRadius:'10px',cursor:'pointer',fontFamily:'inherit',fontWeight:600}}>Back to Home</button></div>;

  const pb = getPriceBreakdown(product.price, product);
  const images = product.images && product.images.length > 0
    ? product.images.map(img => img.image_url || img.thumbnail_url || getPrimaryImage(product))
    : [getPrimaryImage(product)];

  const inWishlist = isInWishlist(product.id);

  return (
    <div className="product-detail-page">
      <button className="back-button" onClick={() => navigate(-1)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Back
      </button>

      <div className="product-detail-container">
        {/* Images */}
        <div className="product-detail-images">
          <div className="product-main-image-container">
            <img src={images[selectedImage]} alt={product.title} className="product-main-image" onError={e=>{e.target.src='https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=600&h=800&fit=crop'}}/>
          </div>
          {images.length > 1 && (
            <div className="product-thumbnails">
              {images.map((img, i) => (
                <img key={i} src={img} alt={`${product.title} ${i+1}`} className={`product-thumbnail ${selectedImage===i?'active':''}`} onClick={()=>setSelectedImage(i)} onError={e=>{e.target.src='https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=200&h=280&fit=crop'}}/>
              ))}
            </div>
          )}
        </div>

        {/* Info - with printable books specific content */}
        <div className="product-detail-info">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'1rem',marginBottom:'1rem'}}>
            <div style={{flex:1}}>
              {product.category_id && <span style={{background:'rgba(230,57,70,0.1)',color:'#e63946',padding:'4px 12px',borderRadius:'20px',fontSize:'0.8rem',fontWeight:600}}>{getCategoryName(product.category_id)}</span>}
              {product.featured && <span style={{background:'linear-gradient(135deg,#e63946,#d00000)',color:'white',padding:'4px 12px',borderRadius:'20px',fontSize:'0.8rem',fontWeight:600,marginLeft:'0.5rem'}}>★ Featured</span>}
            </div>
            <button onClick={()=>onToggleWishlist(product)} style={{background: inWishlist?'rgba(230,57,70,0.1)':'transparent',border:'2px solid'+(inWishlist?'#e63946':'#e2e8f0'),borderRadius:'50%',width:'42px',height:'42px',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color: inWishlist?'#e63946':'#64748b',transition:'all 0.3s',flexShrink:0}}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill={inWishlist?"currentColor":"none"}><path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>

          <h1 className="product-detail-title">{product.title||product.name}</h1>
          <p className="product-detail-author">by <strong>{product.author}</strong></p>

          {/* Pricing */}
          <div className="product-detail-price-section">
            <div className="detail-price-row">
              <span className="detail-mrp">{convertPrice(pb.mrp)}</span>
              <span className="detail-offer-price">{convertPrice(pb.offerPrice)}</span>
              <span className="detail-discount">{pb.discountPercentage}% OFF</span>
            </div>
            <div style={{fontSize:'0.8rem',color:'#64748b',marginBottom:'0.5rem'}}>
              + GST ({pb.gstPercentage}%): {convertPrice(pb.gstAmount)}
            </div>
            <div className="detail-final-price">
              <span>Final: {convertPrice(pb.totalPrice)}</span>
              <span className="converted-price">(₹{pb.totalPrice} INR)</span>
            </div>
            <div style={{fontSize:'0.75rem',color:'#64748b',marginTop:'0.5rem'}}>
              You save {convertPrice(pb.mrp - pb.totalPrice)} ({pb.discountPercentage}% discount applied)
            </div>
          </div>

          {/* Quantity */}
          <div style={{display:'flex',alignItems:'center',gap:'1rem',marginBottom:'1.5rem',padding:'1rem',background:'#f8fafc',borderRadius:'10px',flexWrap:'wrap'}}>
            <span style={{fontWeight:600,color:'#334155'}}>Quantity:</span>
            <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
              <button onClick={()=>setQuantity(q=>Math.max(1,q-1))} style={{width:'32px',height:'32px',border:'2px solid #e2e8f0',borderRadius:'8px',background:'white',cursor:'pointer',fontWeight:700,fontSize:'1.1rem',display:'flex',alignItems:'center',justifyContent:'center'}}>-</button>
              <span style={{fontWeight:700,minWidth:'30px',textAlign:'center',fontSize:'1.1rem'}}>{quantity}</span>
              <button onClick={()=>setQuantity(q=>Math.min(product.stock_quantity||1,q+1))} style={{width:'32px',height:'32px',border:'2px solid #e2e8f0',borderRadius:'8px',background:'white',cursor:'pointer',fontWeight:700,fontSize:'1.1rem',display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
            </div>
            <span className={`stock-status ${product.stock_quantity>10?'stock-in':product.stock_quantity>0?'stock-low':'stock-out'}`}>
              {product.stock_quantity>10?'✓ In Stock':product.stock_quantity>0?`⚠ Only ${product.stock_quantity} left`:'✗ Out of Stock'}
            </span>
          </div>

          {/* Actions */}
          <div className="product-detail-actions">
            <button className="btn add-to-cart" style={{flex:1,padding:'0.9rem 1.5rem',fontSize:'1rem',justifyContent:'center'}} onClick={()=>{for(let i=0;i<quantity;i++)onAddToCart(product)}} disabled={product.stock_quantity<=0}>
              🛒 Add to Cart
            </button>
            <button className="btn buy-now" style={{flex:1,padding:'0.9rem 1.5rem',fontSize:'1rem',justifyContent:'center'}} onClick={()=>onBuyNow(product)} disabled={product.stock_quantity<=0}>
              ⚡ Buy Now
            </button>
          </div>

          {/* Features - Printable books specific */}
          <div style={{display:'flex',flexWrap:'wrap',gap:'0.8rem',marginTop:'1rem'}}>
            {['📥 Instant PDF download','🖨️ Print at home','💾 Reusable','💰 Budget-friendly'].map((f,i)=>(
              <span key={i} style={{background:'rgba(5,150,105,0.08)',color:'#047857',padding:'6px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:500}}>{f}</span>
            ))}
          </div>

          {/* Tabs */}
          <div style={{marginTop:'1.5rem'}}>
            <div style={{display:'flex',gap:'0',borderBottom:'2px solid #e2e8f0',marginBottom:'1rem'}}>
              {['description','details','reviews'].map(tab=>(
                <button key={tab} onClick={()=>setActiveTab(tab)} style={{padding:'0.7rem 1.2rem',border:'none',background:'transparent',cursor:'pointer',fontWeight:600,fontSize:'0.85rem',color:activeTab===tab?'#e63946':'#64748b',borderBottom:activeTab===tab?'2px solid #e63946':'2px solid transparent',marginBottom:'-2px',transition:'all 0.2s',fontFamily:'inherit',textTransform:'capitalize'}}>
                  {tab}
                </button>
              ))}
            </div>

            {activeTab==='description' && (
              <div className="product-detail-description">
                {formatDescription(product.description)}
                {product.long_description && (
                  <div style={{marginTop:'1.5rem'}}>
                    <h4 style={{marginBottom:'0.8rem',color:'#1e293b'}}>Additional Details</h4>
                    {formatDescription(product.long_description)}
                  </div>
                )}
              </div>
            )}

            {activeTab==='details' && (
              <div className="product-detail-meta">
                {[
                  ['ISBN', product.isbn||'N/A'],
                  ['Language', product.language||'English'],
                  ['Pages', product.pages||'N/A'],
                  ['Publisher', product.publisher||'NeoTeeForge'],
                  ['Published', product.published_date ? new Date(product.published_date).getFullYear() : 'N/A'],
                  ['Category', getCategoryName(product.category_id)],
                  ['Format', 'PDF (Digital Download)'],
                  ['File Size', product.file_size ? `${product.file_size} MB` : 'N/A'],
                ].map(([label, value]) => (
                  <div key={label} className="meta-item">
                    <span className="meta-label">{label}</span>
                    <span className="meta-value">{value}</span>
                  </div>
                ))}
              </div>
            )}

            {activeTab==='reviews' && (
              <div>
                {[
                  { name:'Priya S.', rating:5, text:'Absolutely loved this printable book! The activities are engaging and the PDF quality is excellent.' },
                  { name:'Rahul M.', rating:4, text:'Great for home learning. My kids enjoy the activities and I love that I can print multiple copies.' },
                  { name:'Ananya K.', rating:5, text:'One of the best purchases I\'ve made. The instant download feature is super convenient.' },
                ].map((r,i) => (
                  <div key={i} style={{padding:'1rem',background:'#f8fafc',borderRadius:'10px',marginBottom:'0.8rem',borderLeft:'3px solid #e63946'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.5rem'}}>
                      <strong style={{color:'#1e293b'}}>{r.name}</strong>
                      <span style={{color:'#f59e0b'}}>{'⭐'.repeat(r.rating)}</span>
                    </div>
                    <p style={{color:'#64748b',fontSize:'0.85rem',lineHeight:'1.5'}}>{r.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Download feature */}
          <div style={{marginTop:'1.5rem',padding:'1rem',background:'linear-gradient(135deg,rgba(5,150,105,0.08),rgba(4,120,87,0.12))',borderRadius:'12px',border:'1px solid rgba(5,150,105,0.2)'}}>
            <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.5rem'}}>
              <span style={{fontSize:'1.2rem'}}>📥</span>
              <strong style={{color:'#047857'}}>Instant PDF Download</strong>
            </div>
            <p style={{color:'#064e3b',fontSize:'0.85rem',lineHeight:'1.4'}}>After purchase, you can download this printable book immediately. Print at home or at your local copy center. No shipping, no waiting!</p>
          </div>

          {/* Printing tips */}
          <div style={{marginTop:'1rem',padding:'0.8rem',background:'#f8fafc',borderRadius:'8px',fontSize:'0.8rem',color:'#475569',border:'1px solid #e2e8f0'}}>
            <strong style={{color:'#e63946'}}>💡 Printing tip:</strong> Print activity pages in color, reading pages in black & white to save costs.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}